import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL, SERVER_URL } from '../config';
import AuthService from '../services/AuthService';
import '../styles/ProfessorRegistrationForm.css';
import { FaUser, FaGraduationCap, FaFileAlt, FaCheckCircle, FaEye, FaSignOutAlt, FaArrowLeft, FaComments } from 'react-icons/fa';
import LoadingModal from './LoadingModal';
import UserInfo from './UserInfo';
import ProfileSidebar from './ProfileSidebar';
import { getTimeoutSignal } from '../utils/timeoutSignal';
import {
  buildUniversityOption,
  fetchAllEtablissements,
  findEtablissementByValue,
  getEtablissementsErrorMessage,
  normalizeEtablissement,
} from '../services/etablissementsService';
import Select from 'react-select';
import { COUNTRIES } from '../data/countries';

// Configuration du système de brouillon
const DRAFT_KEY = 'localstorageDraft';
const MIGRATED_MATRICULE_KEY = 'migrated-matricule';
const DRAFT_SAVE_DELAY = 1000; // 1 seconde de debounce

const isPrivateEtablissement = (value) => value === 'Privé' || value === 'Établissement Privé';

const MESSAGING_ALLOWED_TYPES = ['Assistant', 'Chef de Travaux', 'Professeur'];
const UNSUPPORTED_ESU_MATRICULE_MESSAGE = 'Les matricules "NU" ou "N.U." ne sont pas pris en charge par la plateforme. Veuillez saisir votre véritable matricule ESU.';

const isUnsupportedEsuMatricule = (value = '') => {
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'nu' || normalized === 'n.u' || normalized === 'n.u.';
};

const normalizeAccountType = (value = '') => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized.includes('assistant')) return 'Assistant';
  if (normalized === 'ct' || normalized.includes('chef')) return 'Chef de Travaux';
  if (normalized.includes('professeur')) return 'Professeur';
  return value;
};

const asArray = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const isOwnMessage = (message, compteId) => {
  const source = message || {};
  const possibleSenderId = (
    source.expediteur_id || source.sender_id || source.auteur_id ||
    source.compte_id || source.current_compte_id || source.expediteur?.id ||
    source.sender?.id || source.auteur?.id
  );

  return possibleSenderId && compteId && String(possibleSenderId) === String(compteId);
};

const getUnreadConversationCount = (conversation, currentCompteId) => {
  const source = conversation || {};
  const numericCount = (
    source.unread_count ??
    source.unread_messages_count ??
    source.messages_non_lus ??
    source.non_lus ??
    source.nombre_non_lus ??
    source.nouveaux_messages ??
    source.nb_nouveaux_messages ??
    source.unread
  );

  if (Number(numericCount) > 0) return Number(numericCount);

  if (source.has_unread || source.a_des_messages_non_lus || source.nouveau_message || source.has_new_message) {
    return 1;
  }

  const lastMessage = source.dernier_message || source.last_message || source.latest_message;
  const lastMessageStatus = String(lastMessage?.statut || lastMessage?.status || lastMessage?.etat || '').toLowerCase();
  const lastMessageRead = (
    lastMessage?.lu ||
    lastMessage?.is_read ||
    lastMessage?.read ||
    lastMessageStatus.includes('lu') ||
    lastMessageStatus.includes('read')
  );

  if (lastMessage && !isOwnMessage(lastMessage, currentCompteId) && !lastMessageRead) {
    return 1;
  }

  return 0;
};

const formatGradeActuel = (grade) => {
  const gradeLabels = {
    PE: 'Professeur Émérite',
    PO: 'Professeur Ordinaire',
    P: 'Professeur',
    PA: 'Professeur Associé',
    DT: 'Docteur à thèse',
  };

  return gradeLabels[grade] || grade;
};

const isAssistantRecherche = (category = '') => {
  const normalized = String(category).trim().toLowerCase();
  return normalized === 'recherche' || normalized === 'assistant de recherche';
};

const shouldShowThirdCycleInfo = (data = {}) => (
  !data.diplome_master_dea_ds &&
  Boolean(data.etablissement_inscription_3cycle || data.decision_inscription || data.date_inscription || data.statut_apprenant)
);

const getUniversityType = (university) => university?.type_etablissement || university?.type_etablissment || '';

const isManualTypeEtablissementAllowed = (universiteAttache) => universiteAttache === 'AUTRES';

// Catégories d'assistant
const CATEGORIE_ASSISTANT_CHOICES = [
  { value: 'Academique', label: 'Assistant Académique' },
  { value: 'Recherche', label: 'Assistant de Recherche' },
];

const ProfessorRegistrationForm = ({ onLogout, currentUser, preselectedType, onRequestAccountTypeReset, formMode, preloadedRecord, onBackToRecord, onOpenMessaging }) => {
  // État initial par défaut
  const defaultFormState = {
    nom: '',
    postnom: '',
    prenom: '',
    sexe: '',
    type_etablissement: '',
    matricule_esu: '',
    matricule: '',
    lieu_naissance: '',
    date_naissance: '',
    grade_actuel: '',
    pays_soutenance: '',
    universite_soutenance: '',
    numero_arrete_equivalence: '',
    copie_arrete_equivalence: null,
    date_soutenance: '',
    type_diplome: '',
    universite_attache: '',
    universite_attache_precisee: '',
    date_engagement: '',
    reference_dernier_arrete: '',
    prime_institutionnelle: '',
    salaire_base: '',
    photo_identite: null,
    possede_diplome: '',
    copie_diplome: null,
    documents_equivalents: null,
    charge_horaire: null,
    domaine_recherche: '',
    sujet_these: '',
    universite_obtention_diplome_doctorat: '',
    pays_obtention_diplome_doctorat: '',
    date_obtention_diplome_doctorat: '',
    commentaire_confirmation: '',
    informations_vraies: false,
    diplome_etat: null,
    diplome_graduat: null,
    diplome_licence: null,
    diplome_master_dea_ds: null,
    has_diplome_etat: '',
    has_diplome_graduat: '',
    has_diplome_licence: '',
    has_inscription_dea_des: '',
    has_diplome_master_dea_ds: '',
    universite_master_dea_ds: '',
    pays_master_dea_ds: '',
    date_obtention_master_dea_ds: '',
    type_diplome_dea_des: '',
    arrete_nomination_ct: null,
    photo_passeport: null,
    type_assistant: '',
    decision_nomination_assistant: null,
    etablissement_inscription_3cycle: null,
    decision_inscription_ass_ct: null,
    date_inscription: '',
    statut: '',
    statut_apprenant: '',
    typecompte: '',
    categorie_assistant: '',
    centre_laboratoire_recherche: '',
  };

  // Initialiser formData en restaurant le brouillon si disponible
  // MAIS si preselectedType est fourni, l'ignorer si le brouillon a un type différent
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        // Si le brouillon n'a pas de typecompte valide, ne pas le charger
        // Car preselectedType va le fournir dans l'useEffect suivant
        if (!draft.typecompte || draft.typecompte === '') {
          return defaultFormState;
        }
        return { ...defaultFormState, ...draft };
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement du brouillon:', error);
    }
    return defaultFormState;
  });

  // Si l'application a pré-sélectionné un type de compte (depuis App.js), l'appliquer
  useEffect(() => {
    if (preselectedType && preselectedType !== '') {
      setFormData((prev) => {
        if (prev.typecompte !== preselectedType) {
          const updated = { ...prev, typecompte: preselectedType };
          return updated;
        }
        return prev;
      });
      setPreviousType(preselectedType);
      setViewType(preselectedType);
    }
  }, [preselectedType]);

  useEffect(() => {
    if (formMode === 'edit') return;

    let migratedMatricule = '';
    try {
      migratedMatricule = (localStorage.getItem(MIGRATED_MATRICULE_KEY) || '').trim();
    } catch (error) {
      console.error('Erreur lors de la récupération du matricule migré:', error);
    }

    if (!migratedMatricule) return;

    setFormData((prev) => {
      const accountType = preselectedType || prev.typecompte;

      if (accountType === 'CT') {
        return { ...prev, matricule: migratedMatricule };
      }

      if (accountType === 'Professeur') {
        return { ...prev, matricule_esu: migratedMatricule };
      }

      return prev;
    });
  }, [formMode, preselectedType]);

  // Si on arrive depuis MyRecord en mode edit, pré-remplir les données
  useEffect(() => {
    if (formMode === 'edit' && preloadedRecord) {
      const d = preloadedRecord;
      const tc = preselectedType || 'Professeur';
      setViewedData(d);
      setEditRecordId(d.id || null);
      setViewMatricule(d.matricule || d.matricule_esu || '');
      setFormData({
        typecompte: tc,
        nom: d.nom || '',
        postnom: d.postnom || '',
        prenom: d.prenom || '',
        sexe: d.sexe || '',
        lieu_naissance: d.lieu_naissance || '',
        date_naissance: d.date_naissance || '',
        domaine_recherche: d.domaine_recherche || '',
        prime_institutionnelle: d.prime_institutionnelle || '',
        salaire_base: d.salaire_base || '',
        commentaire_confirmation: d.commentaires || '',
        informations_vraies: d.informations_vraies || false,
        universite_attache: d.universite_attache || d.etablissement_attache || '',
        universite_attache_precisee: d.universite_attache_precisee || '',
        date_engagement: d.date_engagement || '',
        date_soutenance: tc === 'Professeur' ? '' : (d.date_soutenance || d.date_engagement || ''),
        etablissement_inscription_3cycle: d.etablissement_inscription_3cycle || '',
        date_inscription: d.date_inscription || '',
        statut_apprenant: d.statut_apprenant || '',
        // Diplômes
        diplome_etat: null,
        diplome_graduat: null,
        diplome_licence: null,
        diplome_master_dea_ds: null,
        has_diplome_etat: d.diplome_etat ? 'Oui' : '',
        has_diplome_graduat: d.diplome_graduat ? 'Oui' : '',
        has_diplome_licence: d.diplome_licence ? 'Oui' : '',
        has_inscription_dea_des: !d.diplome_master_dea_ds && (d.etablissement_inscription_3cycle || d.decision_inscription || d.date_inscription || d.statut_apprenant) ? 'Oui' : '',
        has_diplome_master_dea_ds: d.diplome_master_dea_ds ? 'Oui' : '',
        universite_master_dea_ds: d.universite_master_dea_ds || '',
        pays_master_dea_ds: d.pays_master_dea_ds || '',
        date_obtention_master_dea_ds: d.date_obtention_master_dea_ds || '',
        // Professeur
        type_etablissement: d.type_etablissement || '',
        matricule_esu: d.matricule_esu || '',
        grade_actuel: d.grade_actuel || '',
        pays_soutenance: '',
        universite_soutenance: '',
        numero_arrete_equivalence: d.numero_arrete_equivalence || '',
        copie_arrete_equivalence: null,
        type_diplome: d.type_diplome || '',
        type_diplome_dea_des: d.type_diplome_dea_des || '',
        reference_dernier_arrete: d.reference_dernier_arrete || '',
        photo_identite: null,
        possede_diplome: d.possede_diplome || (d.copie_diplome ? 'Oui' : d.documents_equivalents ? 'Non' : ''),
        copie_diplome: null,
        documents_equivalents: null,
        charge_horaire: null,
        sujet_these: d.sujet_these || '',
        copie_these: null,
        // Assistant
        matricule: d.matricule || '',
        statut: d.mandat_assistant || d.statut || '',
        decision_nomination_assistant: null,
        decision_inscription_ass_ct: null,
        photo_passeport: null,
        type_assistant: d.type_assistant || '',
        categorie_assistant: d.categorie_assistant || '',
        centre_laboratoire_recherche: d.centre_laboratoire_recherche || '',
        // CT
        arrete_nomination_ct: null,
      });
      setChangedFields({});
      setEditMode(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode, preloadedRecord]);

  // État pour l'indicateur de sauvegarde
  const [draftSaved, setDraftSaved] = useState(false);

  // Référence pour gérer le debounce
  const saveTimeoutRef = useRef(null);
  const showIndicatorTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showProfileSidebar, setShowProfileSidebar] = useState(false);
  const [messagingUnreadCount, setMessagingUnreadCount] = useState(0);
  const [showTypeChangeModal, setShowTypeChangeModal] = useState(false);
  const [pendingTypeSelection, setPendingTypeSelection] = useState('');
  const [previousType, setPreviousType] = useState('');
  const [viewMatricule, setViewMatricule] = useState('');
  const [viewLoading, setViewLoading] = useState(false);
  const [viewedData, setViewedData] = useState(null);
  const [editRecordId, setEditRecordId] = useState(null);
  const [viewType, setViewType] = useState(preselectedType || 'Professeur');
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState(''); // 'error', 'info', 'success'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(formMode === 'edit');
  const [changedFields, setChangedFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [filePreviews, setFilePreviews] = useState({});
  const [etablissements, setEtablissements] = useState([]);
  const [etablissementsLoading, setEtablissementsLoading] = useState(true);
  const [etablissementsError, setEtablissementsError] = useState('');

  const compteId = currentUser?.id || currentUser?.compte_id;
  const resolvedAccountType = normalizeAccountType(currentUser?.type_de_compte || preselectedType || formData.typecompte);
  const canUseMessaging = MESSAGING_ALLOWED_TYPES.includes(resolvedAccountType);

  const refreshMessagingBadge = useCallback(async () => {
    if (!canUseMessaging || !compteId) {
      setMessagingUnreadCount(0);
      return;
    }

    try {
      const response = await fetch(`${SERVER_URL}/api/enseignants/messagerie/conversations/?current_compte_id=${encodeURIComponent(compteId)}`, {
        signal: getTimeoutSignal(15000),
      });
      if (!response.ok) return;
      const data = await response.json().catch(() => ({}));
      const nextConversations = asArray(data, ['conversations']);
      setMessagingUnreadCount(nextConversations.reduce((total, conversation) => total + getUnreadConversationCount(conversation, compteId), 0));
    } catch (error) {
      console.warn('[Messaging] form badge refresh failed:', error);
    }
  }, [canUseMessaging, compteId]);

  useEffect(() => {
    refreshMessagingBadge();
    const timer = setInterval(refreshMessagingBadge, 12000);
    return () => clearInterval(timer);
  }, [refreshMessagingBadge]);

  useEffect(() => {
    let isMounted = true;

    const loadEtablissements = async () => {
      setEtablissementsLoading(true);
      setEtablissementsError('');

      try {
        const signal = getTimeoutSignal(30000);
        const nextEtablissements = (await fetchAllEtablissements(signal))
          .map(normalizeEtablissement)
          .filter(Boolean);

        if (isMounted) {
          setEtablissements(nextEtablissements);
        }
      } catch (error) {
        if (isMounted) {
          setEtablissements([]);
          setEtablissementsError(getEtablissementsErrorMessage(error));
        }
        console.error('Erreur lors du chargement des établissements:', error);
      } finally {
        if (isMounted) {
          setEtablissementsLoading(false);
        }
      }
    };

    loadEtablissements();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!etablissements.length || !formData.universite_attache) return;

    const selectedEtablissement = findEtablissementByValue(etablissements, formData.universite_attache);
    if (!selectedEtablissement) return;

    const nextUniversiteAttache = selectedEtablissement.denom_etabl;
    const nextTypeEtablissement = getUniversityType(selectedEtablissement) || formData.type_etablissement;
    const shouldNormalizeAttache = formData.universite_attache !== nextUniversiteAttache;
    const shouldNormalizeType = nextTypeEtablissement && formData.type_etablissement !== nextTypeEtablissement;

    if (!shouldNormalizeAttache && !shouldNormalizeType) return;

    setFormData((prev) => ({
      ...prev,
      universite_attache: shouldNormalizeAttache ? nextUniversiteAttache : prev.universite_attache,
      type_etablissement: shouldNormalizeType ? nextTypeEtablissement : prev.type_etablissement,
    }));

    if (editMode) {
      setChangedFields((prev) => ({
        ...prev,
        ...(shouldNormalizeAttache ? { universite_attache: nextUniversiteAttache } : {}),
        ...(shouldNormalizeType ? { type_etablissement: nextTypeEtablissement } : {}),
      }));
    }
  }, [etablissements, formData.universite_attache, formData.type_etablissement, editMode]);

  // Si le formulaire est rendu sans type de compte ET aucun type n'a été pré-sélectionné,
  // demander au parent (App) d'afficher l'écran de sélection global.
  useEffect(() => {
    if (!editMode && (!preselectedType || preselectedType === '')) {
      // Si preselectedType est vide et formData.typecompte est aussi vide, faire une redirection
      if (!formData.typecompte || formData.typecompte === '') {
        if (typeof onRequestAccountTypeReset === 'function') {
          onRequestAccountTypeReset();
        }
      }
    }
  }, [editMode, preselectedType, onRequestAccountTypeReset]);

  const showPopup = (msg, type = 'info') => {
    setPopupMessage(msg);
    setPopupType(type);
    const duration = type === 'error' ? 8000 : 4000;
    setTimeout(() => {
      setPopupMessage('');
      setPopupType('');
    }, duration);
  };

  const readJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();

    if (!text) return {};

    if (contentType.includes('application/json')) {
      return JSON.parse(text);
    }

    try {
      return JSON.parse(text);
    } catch (_) {
      const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      throw new Error(
        plainText
          ? `Réponse non JSON du serveur (${response.status}). ${plainText.slice(0, 180)}`
          : `Réponse non JSON du serveur (${response.status})`
      );
    }
  };

  // Sauvegarder automatiquement le brouillon avec debounce
  useEffect(() => {
    // Annuler le timeout précédent
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Créer un nouveau timeout
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const draftData = { ...formData };
        // Exclure les fichiers binaires
        draftData.photo_identite = null;
        draftData.copie_diplome = null;
        draftData.copie_arrete_equivalence = null;
        draftData.documents_equivalents = null;
        draftData.charge_horaire = null;
        draftData.arrete_nomination_ct = null;
        draftData.photo_passeport = null;
        draftData.decision_nomination_assistant = null;
        draftData.etablissement_inscription_3cycle = null;
        draftData.decision_inscription_ass_ct = null;
        draftData.date_inscription = '';
        draftData.has_inscription_dea_des = '';
        draftData.diplome_etat = null;
        draftData.diplome_graduat = null;
        draftData.diplome_licence = null;
        draftData.diplome_master_dea_ds = null;

        localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));

        // Afficher l'indicateur visuel
        setDraftSaved(true);
        if (showIndicatorTimeoutRef.current) {
          clearTimeout(showIndicatorTimeoutRef.current);
        }
        showIndicatorTimeoutRef.current = setTimeout(() => {
          setDraftSaved(false);
        }, 2000);
      } catch (error) {
        console.error('❌ Erreur lors de la sauvegarde du brouillon:', error);
      }
    }, DRAFT_SAVE_DELAY);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData]);

  // Sauvegarder le brouillon avant de quitter la page
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        const draftData = { ...formData };
        draftData.photo_identite = null;
        draftData.copie_diplome = null;
        draftData.copie_arrete_equivalence = null;
        draftData.documents_equivalents = null;
        draftData.charge_horaire = null;
        draftData.arrete_nomination_ct = null;
        draftData.photo_passeport = null;
        draftData.decision_nomination_assistant = null;
        draftData.etablissement_inscription_3cycle = null;
        draftData.decision_inscription_ass_ct = null;
        draftData.date_inscription = '';
        draftData.has_inscription_dea_des = '';
        draftData.diplome_etat = null;
        draftData.diplome_graduat = null;
        draftData.diplome_licence = null;
        draftData.diplome_master_dea_ds = null;
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
      } catch (error) {
        console.error('Erreur lors de la sauvegarde avant fermeture:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData]);

  // Gestion du changement de type de compte avec confirmation si on risque de perdre des fichiers/champs
  const handleTypecompteChange = (newType) => {
    const currentType = formData.typecompte;
    if (newType === currentType) return;

    const fieldsThatWouldBeCleared = [];
    // Si on quitte Assistant, prévenir si statut ou décision de nomination sont présents
    if (currentType === 'Assistant' && newType !== 'Assistant') {
      if (formData.decision_nomination_assistant) fieldsThatWouldBeCleared.push('Décision de nomination (assistant)');
      if (formData.statut) fieldsThatWouldBeCleared.push('Statut');
      if (formData.categorie_assistant) fieldsThatWouldBeCleared.push("Catégorie d'Assistant");
      if (formData.centre_laboratoire_recherche) fieldsThatWouldBeCleared.push('Centre/Laboratoire de Recherche');
      if (formData.charge_horaire) fieldsThatWouldBeCleared.push('Charge Horaire');
    }
    // Si on quitte CT, prévenir si arrêté de nomination est présent
    if (currentType === 'CT' && newType !== 'CT') {
      if (formData.arrete_nomination_ct) fieldsThatWouldBeCleared.push('Arrêté de nomination (CT)');
    }

    if (fieldsThatWouldBeCleared.length > 0) {
      setPendingTypeSelection(newType);
      setPreviousType(currentType || '');
      setShowTypeChangeModal(true);
      return;
    }

    // Sinon, appliquer directement
    const updatedForm = { ...formData, typecompte: newType };
    setFormData(updatedForm);
    setPreviousType(newType);
    if (editMode) setChangedFields({ ...changedFields, typecompte: newType });
  };
  

  const confirmTypeChange = () => {
    const newType = pendingTypeSelection;
    const cur = previousType;
    const updated = { ...formData };
    // Effacer les champs sensibles selon l'ancien type
    if (cur === 'Assistant' && newType !== 'Assistant') {
      updated.decision_nomination_assistant = null;
      updated.statut = '';
      updated.categorie_assistant = '';
      updated.centre_laboratoire_recherche = '';
      updated.charge_horaire = null;
    }
    if (cur === 'CT' && newType !== 'CT') {
      updated.arrete_nomination_ct = null;
    }

    updated.typecompte = newType;
    setFormData(updated);
    if (editMode) setChangedFields({ ...changedFields, typecompte: newType });
    setShowTypeChangeModal(false);
    setPendingTypeSelection('');
    setPreviousType(newType);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    const isMatriculeField = name === 'matricule' || name === 'matricule_esu';
    const hasUnsupportedMatricule = isMatriculeField && isUnsupportedEsuMatricule(newValue);
    const nextFormData = {
      ...formData,
      [name]: newValue,
    };
    const nextChangedFields = {
      ...changedFields,
      [name]: newValue,
    };

    if (name === 'type_etablissement' && isPrivateEtablissement(newValue)) {
      nextFormData.salaire_base = '';
      nextFormData.matricule = '';
      nextFormData.prime_institutionnelle = '';
      nextChangedFields.salaire_base = '';
      nextChangedFields.matricule = '';
      nextChangedFields.prime_institutionnelle = '';

      if (formData.typecompte === 'Professeur') {
        nextFormData.matricule_esu = '';
        nextChangedFields.matricule_esu = '';
      }
    }

    if (name === 'type_etablissement') {
      const selectedUniversity = findEtablissementByValue(etablissements, formData.universite_attache);
      const selectedUniversityType = getUniversityType(selectedUniversity);

      if (
        formData.universite_attache &&
        !isManualTypeEtablissementAllowed(formData.universite_attache) &&
        selectedUniversityType &&
        selectedUniversityType !== newValue
      ) {
        nextFormData.universite_attache = '';
        nextFormData.universite_attache_precisee = '';
        nextChangedFields.universite_attache = '';
        nextChangedFields.universite_attache_precisee = '';
      }
    }

    if (name === 'grade_actuel' && newValue === 'DT') {
      nextFormData.reference_dernier_arrete = '';
      nextChangedFields.reference_dernier_arrete = '';
    }

    if (name === 'has_diplome_etat') {
      if (newValue !== 'Oui') {
        nextFormData.diplome_etat = null;
        nextChangedFields.diplome_etat = null;
      }
    }

    if (name === 'has_diplome_graduat') {
      if (newValue !== 'Oui') {
        nextFormData.diplome_graduat = null;
        nextChangedFields.diplome_graduat = null;
      }
    }

    if (name === 'has_diplome_licence') {
      if (newValue !== 'Oui') {
        nextFormData.diplome_licence = null;
        nextChangedFields.diplome_licence = null;
      }
    }

    if (name === 'has_diplome_master_dea_ds') {
      if (newValue === 'Oui') {
        nextFormData.has_inscription_dea_des = '';
        nextFormData.etablissement_inscription_3cycle = '';
        nextFormData.decision_inscription_ass_ct = null;
        nextFormData.date_inscription = '';
        nextFormData.statut_apprenant = '';
        nextChangedFields.has_inscription_dea_des = '';
        nextChangedFields.etablissement_inscription_3cycle = '';
        nextChangedFields.decision_inscription_ass_ct = null;
        nextChangedFields.date_inscription = '';
        nextChangedFields.statut_apprenant = '';
      } else {
        nextFormData.diplome_master_dea_ds = null;
        nextFormData.type_diplome = '';
        nextFormData.type_diplome_dea_des = '';
        nextFormData.universite_master_dea_ds = '';
        nextFormData.pays_master_dea_ds = '';
        nextFormData.date_obtention_master_dea_ds = '';
        nextChangedFields.diplome_master_dea_ds = null;
        nextChangedFields.type_diplome = '';
        nextChangedFields.type_diplome_dea_des = '';
        nextChangedFields.universite_master_dea_ds = '';
        nextChangedFields.pays_master_dea_ds = '';
        nextChangedFields.date_obtention_master_dea_ds = '';
        if (newValue !== 'Non') {
          nextFormData.has_inscription_dea_des = '';
          nextFormData.etablissement_inscription_3cycle = '';
          nextFormData.decision_inscription_ass_ct = null;
          nextFormData.date_inscription = '';
          nextFormData.statut_apprenant = '';
          nextChangedFields.has_inscription_dea_des = '';
          nextChangedFields.etablissement_inscription_3cycle = '';
          nextChangedFields.decision_inscription_ass_ct = null;
          nextChangedFields.date_inscription = '';
          nextChangedFields.statut_apprenant = '';
        }
      }
    }

    if (name === 'has_inscription_dea_des' && newValue !== 'Oui') {
      nextFormData.etablissement_inscription_3cycle = '';
      nextFormData.decision_inscription_ass_ct = null;
      nextFormData.date_inscription = '';
      nextFormData.statut_apprenant = '';
      nextChangedFields.etablissement_inscription_3cycle = '';
      nextChangedFields.decision_inscription_ass_ct = null;
      nextChangedFields.date_inscription = '';
      nextChangedFields.statut_apprenant = '';
    }

    if (name === 'possede_diplome') {
      if (newValue !== 'Oui') {
        nextFormData.copie_diplome = null;
        nextChangedFields.copie_diplome = null;
      }

      if (newValue !== 'Non') {
        nextFormData.documents_equivalents = null;
        nextChangedFields.documents_equivalents = null;
      }
    }

    setFormData(nextFormData);
    if (isMatriculeField && e.target.setCustomValidity) {
      e.target.setCustomValidity(hasUnsupportedMatricule ? UNSUPPORTED_ESU_MATRICULE_MESSAGE : '');
      if (hasUnsupportedMatricule) e.target.reportValidity();
    }

    if (hasUnsupportedMatricule) {
      setFieldErrors(prev => ({ ...prev, [name]: UNSUPPORTED_ESU_MATRICULE_MESSAGE }));
    } else if (fieldErrors[name]) {
      setFieldErrors(prev => { const next = {...prev}; delete next[name]; return next; });
    }
    const errEl = document.getElementById(name);
    if (errEl) {
      errEl.classList.toggle('field-has-error', hasUnsupportedMatricule);
    }
    if (editMode) {
      setChangedFields(nextChangedFields);
    }
  };

  // ── Constantes fichiers ─────────────────────────────────────────────────
  const PHOTO_FIELDS = new Set(['photo_passeport', 'photo_identite']);
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 Mo

  // Documents (PDF, JPG, JPEG, PNG)
  const DOC_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
  const DOC_ACCEPT     = DOC_EXTENSIONS.join(',');
  const DOC_LABEL      = 'Formats acceptés : .pdf, .jpg, .jpeg, .png (max 10 Mo)';
  const DOC_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

  // Photos uniquement (pas de PDF)
  const PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
  const PHOTO_ACCEPT     = PHOTO_EXTENSIONS.join(',');
  const PHOTO_LABEL      = 'Formats acceptés : .jpg, .jpeg, .png (max 10 Mo)';
  const PHOTO_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

  // Rétro-compatibilité pour les champs documents existants
  const ALLOWED_FILE_ACCEPT = DOC_ACCEPT;
  const ALLOWED_FILE_LABEL  = DOC_LABEL;

  /** Détecte le type MIME réel via les magic bytes (premiers octets du fichier). */
  const detectMimeFromBytes = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const bytes = new Uint8Array(ev.target.result);
        if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
          resolve('application/pdf'); // %PDF
        } else if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
          resolve('image/png'); // PNG
        } else if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
          resolve('image/jpeg'); // JPEG / JPG
        } else {
          resolve(null); // non reconnu
        }
      };
      reader.onerror = () => resolve(null);
      reader.readAsArrayBuffer(file.slice(0, 8));
    });

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;
    const file = files[0];

    const isPhoto     = PHOTO_FIELDS.has(name);
    const allowedExt  = isPhoto ? PHOTO_EXTENSIONS : DOC_EXTENSIONS;
    const allowedMime = isPhoto ? PHOTO_MIME_TYPES  : DOC_MIME_TYPES;
    const hintLabel   = isPhoto ? PHOTO_LABEL       : DOC_LABEL;

    // 1. Taille maximale (10 Mo)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showPopup('Le fichier dépasse la taille maximale autorisée (10 Mo).', 'error');
      e.target.value = '';
      return;
    }

    // 2. Extension
    const fileName = file.name.toLowerCase();
    if (!allowedExt.some(ext => fileName.endsWith(ext))) {
      showPopup(`Format de fichier non autorisé. ${hintLabel}`, 'error');
      e.target.value = '';
      return;
    }

    // 3. Type MIME réel (magic bytes) — détecte les fichiers corrompus ou renommés
    const detectedMime = await detectMimeFromBytes(file);
    if (!detectedMime || !allowedMime.has(detectedMime)) {
      showPopup(`Le fichier semble corrompu ou son contenu ne correspond pas à son extension. ${hintLabel}`, 'error');
      e.target.value = '';
      return;
    }

    // 4. Aperçu pour les champs photo
    if (isPhoto) {
      const newUrl = URL.createObjectURL(file);
      setFilePreviews(prev => {
        if (prev[name]) URL.revokeObjectURL(prev[name]);
        return { ...prev, [name]: newUrl };
      });
    }

    setFormData(prev => ({ ...prev, [name]: file }));
    // Effacer l'erreur de validation
    if (fieldErrors[name]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
    const errEl = document.getElementById(name);
    if (errEl) errEl.classList.remove('field-has-error');
    if (editMode) {
      setChangedFields(prev => ({ ...prev, [name]: file }));
    }
  };

  const handleDiplomaCountryChange = (fieldName, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';

    setFormData({
      ...formData,
      [fieldName]: value,
    });
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
    if (editMode) {
      setChangedFields({
        ...changedFields,
        [fieldName]: value,
      });
    }
  };

  // Handler pour le changement de l'établissement d'attache
  const handleUniversityChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    const selectedUniversity = findEtablissementByValue(etablissements, value);
    const typeEtablissement = getUniversityType(selectedUniversity);
    const nextTypeEtablissement = typeEtablissement || formData.type_etablissement;
    const privateTypeSelected = isPrivateEtablissement(nextTypeEtablissement);

    setFormData({
      ...formData,
      universite_attache: value,
      universite_attache_precisee: value ? formData.universite_attache_precisee : '',
      type_etablissement: nextTypeEtablissement,
      matricule: privateTypeSelected ? '' : formData.matricule,
      matricule_esu: privateTypeSelected ? '' : formData.matricule_esu,
      salaire_base: privateTypeSelected ? '' : formData.salaire_base,
      prime_institutionnelle: privateTypeSelected ? '' : formData.prime_institutionnelle,
    });
    if (fieldErrors.universite_attache) {
      setFieldErrors(prev => { const next = {...prev}; delete next.universite_attache; return next; });
    }
    if (fieldErrors.type_etablissement && nextTypeEtablissement) {
      setFieldErrors(prev => { const next = {...prev}; delete next.type_etablissement; return next; });
    }
    if (editMode) {
      setChangedFields({
        ...changedFields,
        universite_attache: value,
        universite_attache_precisee: value ? formData.universite_attache_precisee : '',
        type_etablissement: nextTypeEtablissement,
        ...(privateTypeSelected ? {
          matricule: '',
          matricule_esu: '',
          salaire_base: '',
          prime_institutionnelle: '',
        } : {}),
      });
    }
  };

  // Convertir la liste des établissements en options pour react-select
  const allUniversityOptions = etablissements.map(buildUniversityOption);
  const universityOptions = etablissements
    .filter((uni) => {
      if (!formData.type_etablissement) return true;
      if (isManualTypeEtablissementAllowed(uni.denom_etabl)) return true;
      return getUniversityType(uni) === formData.type_etablissement;
    })
    .map(buildUniversityOption);
  const selectedUniversityOption = formData.universite_attache
    ? allUniversityOptions.find((option) => (
        option.value === formData.universite_attache ||
        option.sigle === formData.universite_attache
      )) || {
        value: formData.universite_attache,
        label: formData.universite_attache,
      }
    : null;
  const isUniversitySelectDisabled = etablissementsLoading;
  const universitySelectPlaceholder = isUniversitySelectDisabled
    ? (
      etablissementsLoading
        ? 'Chargement des établissements...'
        : etablissementsError
    )
    : etablissementsLoading
      ? 'Chargement des établissements...'
      : etablissementsError
        ? etablissementsError
        : "Sélectionner ou rechercher un établissement...";
  const universityNoOptionsMessage = () => (
    etablissementsLoading
      ? 'Chargement des établissements...'
      : etablissementsError || 'Aucun établissement trouvé'
  );

  const existingFileData = editMode ? (preloadedRecord || viewedData || {}) : {};
  const formatEtablissementAttache = (value) => {
    const etablissement = findEtablissementByValue(etablissements, value);
    return etablissement?.denom_etabl || value;
  };

  const getExistingFilePath = (fieldName) => {
    const fieldMap = {
      arrete_nomination_ct: 'arrete_nomination',
      decision_inscription_ass_ct: 'decision_inscription',
      decision_nomination_assistant: 'decision_nomination',
    };
    return existingFileData[fieldName] || existingFileData[fieldMap[fieldName]] || '';
  };
  const hasCurrentFile = (fieldName) => Boolean(formData[fieldName] || getExistingFilePath(fieldName));
  const getFileNameFromPath = (path) => String(path).split('/').pop() || String(path);
  const getFileUrl = (path) => String(path).startsWith('http') ? path : `${SERVER_URL}${path}`;
  const renderFileStatus = (fieldName) => {
    const selectedFile = formData[fieldName];
    if (typeof File !== 'undefined' && selectedFile instanceof File) {
      return <small className="file-info">✅ Nouveau fichier sélectionné: {selectedFile.name}</small>;
    }

    const existingPath = getExistingFilePath(fieldName);
    if (!existingPath) return null;

    const fileName = getFileNameFromPath(existingPath);
    return (
      <small className="file-info">
        Fichier actuel:&nbsp;
        <a href={getFileUrl(existingPath)} target="_blank" rel="noopener noreferrer" className="file-link" title={fileName}>
          {fileName}
        </a>
      </small>
    );
  };

  const handleViewData = async () => {
    if (!viewMatricule.trim()) {
      showPopup('Veuillez entrer un matricule', 'error');
      return;
    }

    setViewLoading(true);
    try {
      let endpoint = '';
      
      // Construire l'endpoint en fonction du type (nouveaux endpoints)
      if (viewType === 'Professeur') {
        endpoint = `/api/bnn/enseignant/professeur/${viewMatricule}/`;
      } else if (viewType === 'Assistant') {
        endpoint = `/api/enseignants/assistant/${viewMatricule}/`;
      } else if (viewType === 'CT') {
        endpoint = `/api/enseignants/chef-travaux/${viewMatricule}/`;
      }

      const response = await fetch(`${SERVER_URL}${endpoint}`, {
        signal: getTimeoutSignal(240000), // 4 minutes
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `${viewType} non trouvé`);
      }

      const data = await response.json();
      setViewedData(data);
    } catch (error) {
      let errorMsg = 'Erreur lors de la recherche';
      if (error.name === 'AbortError') {
        errorMsg = 'La requête a expiré. Veuillez réessayer.';
      } else if (error.message === 'Failed to fetch') {
        console.error('Erreur de connexion réseau lors de la recherche');
        errorMsg = 'Erreur de connexion au serveur';
        return;
      } else {
        errorMsg = error.message;
      }
      showPopup(errorMsg, 'error');
      setViewedData(null);
      console.error('Erreur complète:', error);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewMatricule('');
    setViewType('Professeur');
    setViewedData(null);
  };

  const handleDeleteProfessor = async () => {
    const retryFetch = async (url, options, maxRetries = 2) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const response = await fetch(url, {
            ...options,
            signal: getTimeoutSignal(240000), // 4 minutes
          });
          return response;
        } catch (error) {
          if (i === maxRetries - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };

    try {
      const response = await retryFetch(`${API_BASE_URL}enseignant/delete/${viewMatricule}/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      showPopup('Professeur supprimé avec succès', 'success');
      setShowDeleteConfirm(false);
      handleCloseViewModal();
    } catch (error) {
      let errorMsg = 'Erreur lors de la suppression';
      if (error.name === 'AbortError') {
        errorMsg = 'La requête a expiré';
      } else if (error.message === 'Failed to fetch') {
        console.error('Erreur de connexion réseau lors de la suppression');
        return;
      }
      showPopup(errorMsg, 'error');
      console.error('Erreur complète:', error);
    }
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Fonction helper pour rendre le contenu du modal dynamiquement
  const renderModalContent = () => {
    if (!viewedData) return null;

    const isEmptyDisplayValue = (value) => {
      if (value === null || value === undefined) return true;
      if (typeof value !== 'string') return false;

      const normalized = value.trim().toLowerCase();
      return (
        normalized === '' ||
        normalized === 'non renseigné' ||
        normalized === 'non renseigne' ||
        normalized === 'non fourni' ||
        normalized === 'aucun commentaire' ||
        normalized === 'null' ||
        normalized === 'undefined' ||
        normalized === 'n/a' ||
        normalized === 'na'
      );
    };

    const DataRow = ({ label, value, hideEmpty = true }) => {
      if (hideEmpty && isEmptyDisplayValue(value)) return null;

      return (
        <div className="data-row">
          <span className="data-label">{label}:</span>
          <span className="data-value">{value}</span>
        </div>
      );
    };

    const formatDisplaySexe = (sexe) => {
      if (isEmptyDisplayValue(sexe)) return '';
      return sexe === 'M' ? 'Masculin' : sexe === 'F' ? 'Féminin' : sexe;
    };

    const FileLink = ({ path }) => {
      if (isEmptyDisplayValue(path)) return null;
      const fileName = path.split('/').pop();
      const fullUrl = path.startsWith('http') ? path : `${SERVER_URL}${path}`;
      return (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="file-link" title={fileName}>
          📄 {fileName}
        </a>
      );
    };

    const FileDataRow = ({ label, path }) => {
      if (isEmptyDisplayValue(path)) return null;
      return (
        <div className="data-row">
          <span className="data-label">{label}:</span>
          <span className="data-value"><FileLink path={path} /></span>
        </div>
      );
    };

    const DataSection = ({ title, children }) => (
      <div className="data-section">
        <h3>{title}</h3>
        {children}
      </div>
    );

    // Contenu pour Professeur
    if (viewType === 'Professeur') {
      return (
        <div className="modal-data">
          <DataSection title="Informations Personnelles">
            <DataRow label="Nom" value={viewedData.nom} />
            <DataRow label="Postnom" value={viewedData.postnom} />
            <DataRow label="Prénom" value={viewedData.prenom} />
            <DataRow label="Sexe" value={formatDisplaySexe(viewedData.sexe)} />
            {!isPrivateEtablissement(viewedData.type_etablissement) && (
              <DataRow label="Matricule ESU" value={viewedData.matricule_esu} />
            )}
            <DataRow label="Type de compte" value={viewedData.typecompte || 'Professeur'} />
            <DataRow label="Lieu de naissance" value={viewedData.lieu_naissance} />
            <DataRow label="Date de naissance" value={viewedData.date_naissance} />
          </DataSection>

          <DataSection title="Informations Administratives">
            <DataRow label="Type Établissement" value={viewedData.type_etablissement === 'Public' ? 'Établissement Public' : viewedData.type_etablissement === 'Privé' ? 'Établissement Privé' : viewedData.type_etablissement} />
            <DataRow label="Qualité ou Grade Académique" value={formatGradeActuel(viewedData.grade_actuel)} />
            <DataRow label="Établissement d'attache" value={formatEtablissementAttache(viewedData.universite_attache_precisee || viewedData.universite_attache)} />
            <DataRow label="Domaine de recherche" value={viewedData.domaine_recherche} />
          </DataSection>

          <DataSection title="Informations de Soutenance">
            <DataRow label="Type de diplôme de Doctorat" value={viewedData.type_diplome} />
            <DataRow label="Numéro arrêté équivalence" value={viewedData.numero_arrete_equivalence} />
          </DataSection>

          <DataSection title="Diplômes par niveau">
            <FileDataRow label="Diplôme d'État" path={viewedData.diplome_etat} />
            <FileDataRow label="Diplôme de Graduat" path={viewedData.diplome_graduat} />
            <FileDataRow label="Diplôme de Licence" path={viewedData.diplome_licence} />
            <FileDataRow label="Diplôme de Master/D.E.A/D.E.S" path={viewedData.diplome_master_dea_ds} />
            <DataRow label="Université d'obtention de votre master/D.E.A/D.E.S" value={viewedData.universite_master_dea_ds} />
            <DataRow label="Pays d'obtention de votre Master/D.E.A/D.E.S" value={viewedData.pays_master_dea_ds} />
            <DataRow label="Date d'obtention de votre Master/D.E.A/D.E.S" value={viewedData.date_obtention_master_dea_ds} />
            <DataRow label="Université d'obtention de votre Doctorat" value={viewedData.universite_obtention_diplome_doctorat} />
            <DataRow label="Pays d'obtention de votre Doctorat" value={viewedData.pays_obtention_diplome_doctorat} />
            <DataRow label="Date d'obtention de votre Doctorat" value={viewedData.date_obtention_diplome_doctorat} />
            <FileDataRow label="Copie du Diplôme de Doctorat ou Document équivalent" path={viewedData.copie_diplome} />
            <FileDataRow label="Charge horaire" path={viewedData.charge_horaire} />
          </DataSection>

          {viewedData.created_at && (
            <DataSection title="Métadonnées">
              <DataRow label="Date de création" value={new Date(viewedData.created_at).toLocaleDateString('fr-FR')} />
              <DataRow label="Dernière mise à jour" value={viewedData.updated_at ? new Date(viewedData.updated_at).toLocaleDateString('fr-FR') : ''} />
            </DataSection>
          )}
        </div>
      );
    }

    // Contenu pour Assistant
    if (viewType === 'Assistant') {
      return (
        <div className="modal-data">
          <DataSection title="Informations Personnelles">
            {!isPrivateEtablissement(viewedData.type_etablissement) && (
              <DataRow label="Matricule" value={viewedData.matricule} />
            )}
            <DataRow label="Nom" value={viewedData.nom} />
            <DataRow label="Postnom" value={viewedData.postnom} />
            <DataRow label="Prenom" value={viewedData.prenom} hideEmpty />
            <DataRow label="Sexe" value={formatDisplaySexe(viewedData.sexe)} hideEmpty />
            <DataRow label="Date de naissance" value={viewedData.date_naissance} hideEmpty />
            <DataRow label="Lieu de naissance" value={viewedData.lieu_naissance} hideEmpty />
          </DataSection>

          <DataSection title="Informations Administratives">
            <DataRow label="Date d'engagement" value={viewedData.date_engagement} hideEmpty />
            <DataRow label="Domaine de recherche" value={viewedData.domaine_recherche} hideEmpty />
            <DataRow label="Établissement d'attache" value={formatEtablissementAttache(viewedData.etablissement_attache)} hideEmpty />
            <DataRow label="Mandat Assistant" value={viewedData.mandat_assistant} hideEmpty />
            <DataRow label="Catégorie d'Assistant" value={viewedData.categorie_assistant} hideEmpty />
            {viewedData.categorie_assistant === 'Recherche' && (
              <DataRow label="Centre/Laboratoire de Recherche" value={viewedData.centre_laboratoire_recherche} hideEmpty />
            )}
            {!isPrivateEtablissement(viewedData.type_etablissement) && (
              <DataRow label="Prime institutionnelle" value={viewedData.prime_institutionnelle} hideEmpty />
            )}
          </DataSection>

          {shouldShowThirdCycleInfo(viewedData) && (
            <DataSection title="Inscriptions et Statut">
              <DataRow label="Établissement inscription 3e cycle" value={viewedData.etablissement_inscription_3cycle} hideEmpty />
              <DataRow label="Date d'inscription" value={viewedData.date_inscription} hideEmpty />
              <DataRow label="Statut d'apprenant" value={viewedData.statut_apprenant} hideEmpty />
            </DataSection>
          )}

          <DataSection title="Documents">
            <FileDataRow label="Photo passeport" path={viewedData.photo_passeport} />
            <FileDataRow label="Décision de nomination" path={viewedData.decision_nomination} />
            {shouldShowThirdCycleInfo(viewedData) && (
              <FileDataRow label="Décision d'inscription" path={viewedData.decision_inscription} />
            )}
            {!isAssistantRecherche(viewedData.categorie_assistant) && (
              <FileDataRow label="Charge horaire" path={viewedData.charge_horaire} />
            )}
          </DataSection>

          <DataSection title="Confirmation">
            <DataRow label="Commentaires" value={viewedData.commentaires} hideEmpty />
            <DataRow label="Informations confirmées" value={viewedData.informations_vraies ? 'Oui ✓' : 'Non ✗'} />
          </DataSection>

          {viewedData.created_at && (
            <DataSection title="Métadonnées">
              <DataRow label="Date de création" value={new Date(viewedData.created_at).toLocaleDateString('fr-FR')} />
            </DataSection>
          )}
        </div>
      );
    }

    // Contenu pour Chef de Travaux
    if (viewType === 'CT') {
      return (
        <div className="modal-data">
          <DataSection title="Informations Personnelles">
            {!isPrivateEtablissement(viewedData.type_etablissement) && (
              <DataRow label="Matricule" value={viewedData.matricule} />
            )}
            <DataRow label="Nom" value={viewedData.nom} />
            <DataRow label="Postnom" value={viewedData.postnom} />
            <DataRow label="Prenom" value={viewedData.prenom} hideEmpty />
            <DataRow label="Sexe" value={formatDisplaySexe(viewedData.sexe)} hideEmpty />
            <DataRow label="Date de naissance" value={viewedData.date_naissance} hideEmpty />
            <DataRow label="Lieu de naissance" value={viewedData.lieu_naissance} hideEmpty />
          </DataSection>

          <DataSection title="Informations Administratives">
            <DataRow label="Date d'engagement" value={viewedData.date_engagement} hideEmpty />
            <DataRow label="Domaine de recherche" value={viewedData.domaine_recherche} hideEmpty />
            <DataRow label="Établissement d'attache" value={formatEtablissementAttache(viewedData.etablissement_attache)} hideEmpty />
            <DataRow label="Type d'établissement" value={viewedData.type_etablissement === 'Public' ? 'Établissement Public' : viewedData.type_etablissement === 'Privé' ? 'Établissement Privé' : viewedData.type_etablissement} hideEmpty />
            {!isPrivateEtablissement(viewedData.type_etablissement) && (
              <DataRow label="Prime institutionnelle" value={viewedData.prime_institutionnelle} hideEmpty />
            )}
          </DataSection>

          {shouldShowThirdCycleInfo(viewedData) && (
            <DataSection title="Inscriptions et Statut">
              <DataRow label="Établissement inscription 3e cycle" value={viewedData.etablissement_inscription_3cycle} hideEmpty />
              <DataRow label="Date d'inscription" value={viewedData.date_inscription} hideEmpty />
              <DataRow label="Statut d'apprenant" value={viewedData.statut_apprenant} hideEmpty />
            </DataSection>
          )}

          <DataSection title="Documents">
            <FileDataRow label="Arrêté de nomination" path={viewedData.arrete_nomination} />
            <FileDataRow label="Photo passeport" path={viewedData.photo_passeport} />
            {shouldShowThirdCycleInfo(viewedData) && (
              <FileDataRow label="Décision d'inscription" path={viewedData.decision_inscription} />
            )}
            <FileDataRow label="Charge horaire" path={viewedData.charge_horaire} />
          </DataSection>

          <DataSection title="Confirmation">
            <DataRow label="Commentaires" value={viewedData.commentaires} hideEmpty />
            <DataRow label="Informations confirmées" value={viewedData.informations_vraies ? 'Oui ✓' : 'Non ✗'} />
          </DataSection>

          {viewedData.created_at && (
            <DataSection title="Métadonnées">
              <DataRow label="Date de création" value={new Date(viewedData.created_at).toLocaleDateString('fr-FR')} />
            </DataSection>
          )}
        </div>
      );
    }

    return null;
  };

  const handleStartEdit = () => {
    setEditMode(true);
    // typecompte drives which form sections are shown
    const tc = viewType === 'CT' ? 'CT' : viewType === 'Assistant' ? 'Assistant' : 'Professeur';
    setFormData({
      typecompte: tc,
      // ── Common personal fields ────────────────────────────────────────────
      nom: viewedData.nom || '',
      postnom: viewedData.postnom || '',
      prenom: viewedData.prenom || '',
      sexe: viewedData.sexe || '',
      lieu_naissance: viewedData.lieu_naissance || '',
      date_naissance: viewedData.date_naissance || '',
      domaine_recherche: viewedData.domaine_recherche || '',
      prime_institutionnelle: viewedData.prime_institutionnelle || '',
      salaire_base: viewedData.salaire_base || '',
      commentaire_confirmation: viewedData.commentaire_confirmation || viewedData.commentaires || '',
      informations_vraies: viewedData.informations_vraies || false,
      universite_attache: viewedData.universite_attache || viewedData.etablissement_attache || '',
      universite_attache_precisee: viewedData.universite_attache_precisee || '',
      date_engagement: viewedData.date_engagement || '',
      date_soutenance: tc === 'Professeur' ? '' : (viewedData.date_soutenance || viewedData.date_engagement || ''),
      etablissement_inscription_3cycle: viewedData.etablissement_inscription_3cycle || '',
      date_inscription: viewedData.date_inscription || '',
      statut_apprenant: viewedData.statut_apprenant || '',
      // Diploma toggles (common)
      diplome_etat: null,
      diplome_graduat: null,
      diplome_licence: null,
      diplome_master_dea_ds: null,
      has_diplome_etat: viewedData.diplome_etat ? 'Oui' : '',
      has_diplome_graduat: viewedData.diplome_graduat ? 'Oui' : '',
      has_diplome_licence: viewedData.diplome_licence ? 'Oui' : '',
      has_inscription_dea_des: !viewedData.diplome_master_dea_ds && (viewedData.etablissement_inscription_3cycle || viewedData.decision_inscription || viewedData.date_inscription || viewedData.statut_apprenant) ? 'Oui' : '',
      has_diplome_master_dea_ds: viewedData.diplome_master_dea_ds ? 'Oui' : '',
      universite_master_dea_ds: viewedData.universite_master_dea_ds || '',
      pays_master_dea_ds: viewedData.pays_master_dea_ds || '',
      date_obtention_master_dea_ds: viewedData.date_obtention_master_dea_ds || '',
      // ── Professeur-specific ───────────────────────────────────────────────
      type_etablissement: viewedData.type_etablissement || '',
      matricule_esu: viewedData.matricule_esu || '',
      grade_actuel: viewedData.grade_actuel || '',
      pays_soutenance: '',
      universite_soutenance: '',
      numero_arrete_equivalence: viewedData.numero_arrete_equivalence || '',
      copie_arrete_equivalence: null,
      type_diplome: viewedData.type_diplome || '',
      type_diplome_dea_des: viewedData.type_diplome_dea_des || '',
      reference_dernier_arrete: viewedData.reference_dernier_arrete || '',
      photo_identite: null,
      possede_diplome: viewedData.possede_diplome || (viewedData.copie_diplome ? 'Oui' : viewedData.documents_equivalents ? 'Non' : ''),
      copie_diplome: null,
      documents_equivalents: null,
      charge_horaire: null,
      sujet_these: viewedData.sujet_these || '',
      copie_these: null,
      // ── Assistant-specific ────────────────────────────────────────────────
      matricule: viewedData.matricule || '',
      statut: viewedData.mandat_assistant || viewedData.statut || '',
      decision_nomination_assistant: null,
      decision_inscription_ass_ct: null,
      photo_passeport: null,
      type_assistant: viewedData.type_assistant || '',
      categorie_assistant: viewedData.categorie_assistant || '',
      centre_laboratoire_recherche: viewedData.centre_laboratoire_recherche || '',
      // ── CT-specific ───────────────────────────────────────────────────────
      arrete_nomination_ct: null,
    });
    setChangedFields({});
    setEditRecordId(viewedData.id || null);
    setShowViewModal(false);
  };

  const handleEditProfessor = async () => {
    // En mode édition on envoie uniquement les champs modifiés (PATCH partiel) :
    // aucune validation bloquante n'est nécessaire.
    if (Object.keys(changedFields).length === 0) {
      showPopup('Aucune modification détectée', 'info');
      return;
    }

    setLoading(true);

    const retryFetch = async (url, options, maxRetries = 2) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const response = await fetch(url, {
            ...options,
            signal: getTimeoutSignal(240000), // 4 minutes timeout pour PATCH
          });
          return response;
        } catch (error) {
          if (i === maxRetries - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };

    try {
      const submitData = new FormData();

      const getEtablissementAttacheApiField = () => (
        formData.typecompte === 'Professeur' ? 'universite_attache' : 'etablissement_attache'
      );

      // Mapping des noms de champs formulaire → noms API pour Assistant
      const ASSISTANT_FIELD_MAP = {
        decision_nomination_assistant: 'decision_nomination',
        decision_inscription_ass_ct:   'decision_inscription',
      };

      // Mapping des noms de champs formulaire → noms API pour CT
      const CT_FIELD_MAP = {
        date_soutenance:            'date_engagement',
        arrete_nomination_ct:       'arrete_nomination',
        decision_inscription_ass_ct:'decision_inscription',
        commentaire_confirmation:   'commentaires',
      };

      // Ajouter uniquement les champs modifiés
      Object.keys(changedFields).forEach((key) => {
        let value = changedFields[key];

        // Si c'est le champ universite_attache et que la valeur est "AUTRES",
        // utiliser la valeur du champ de précision à la place
        if (key === 'universite_attache' && value === 'AUTRES') {
          value = formData.universite_attache_precisee;
        }

        const isEtablissementAttacheField = (
          key === 'universite_attache' ||
          (key === 'universite_attache_precisee' && formData.universite_attache === 'AUTRES')
        );

        // Pour CT, remapper les noms de champs formulaire vers les noms API
        const apiKey = isEtablissementAttacheField
          ? getEtablissementAttacheApiField()
          : (
            formData.typecompte === 'Assistant' && ASSISTANT_FIELD_MAP[key]
              ? ASSISTANT_FIELD_MAP[key]
              : formData.typecompte === 'CT' && CT_FIELD_MAP[key] ? CT_FIELD_MAP[key] : key
          );
        if (formData.type_etablissement === 'Privé' && ['salaire_base', 'matricule', 'prime_institutionnelle'].includes(apiKey)) {
          return;
        }

        if (
          formData.typecompte === 'Professeur' &&
          formData.type_etablissement === 'Privé' &&
          apiKey === 'matricule_esu'
        ) {
          return;
        }

        if (changedFields[key] instanceof File) {
          submitData.append(apiKey, changedFields[key]);
        } else if (value !== null && value !== undefined) {
          submitData.append(apiKey, value);
        }
      });

      if (!editRecordId) {
        throw new Error("Impossible de modifier ce dossier : l'identifiant est introuvable.");
      }

      const editUrl = formData.typecompte === 'Assistant'
        ? `${SERVER_URL}/api/enseignants/assistant/edit/${editRecordId}/`
        : formData.typecompte === 'CT'
          ? `${SERVER_URL}/api/enseignants/chef-travaux/edit/${editRecordId}/`
          : `${SERVER_URL}/api/enseignants/professeur/edit/${editRecordId}/`;

      const response = await retryFetch(editUrl, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
        },
        body: submitData,
      });

      if (!response.ok) {
        const errorData = await readJsonResponse(response);
        throw new Error(errorData.message || errorData.detail || 'Erreur lors de la modification');
      }

      await readJsonResponse(response);
      showPopup('Dossier modifié avec succès', 'success');
      setEditMode(false);
      setChangedFields({});
      // Si on vient de MyRecord, y retourner après 800ms
      if (onBackToRecord) {
        setTimeout(() => onBackToRecord(), 800);
        return;
      }
      // Réinitialiser le formulaire
      setFormData({
        nom: '',
        postnom: '',
        prenom: '',
        sexe: '',
        type_etablissement: '',
        matricule_esu: '',
        lieu_naissance: '',
        date_naissance: '',
        grade_actuel: '',
        pays_soutenance: '',
        universite_soutenance: '',
        numero_arrete_equivalence: '',
        copie_arrete_equivalence: null,
        date_soutenance: '',
        type_diplome: '',
        universite_attache: '',
        universite_attache_precisee: '',
        reference_dernier_arrete: '',
        prime_institutionnelle: '',
        salaire_base: '',
        photo_identite: null,
        possede_diplome: '',
        copie_diplome: null,
        documents_equivalents: null,
        charge_horaire: null,
        domaine_recherche: '',
        sujet_these: '',
        commentaire_confirmation: '',
        informations_vraies: false,
        diplome_etat: null,
        diplome_graduat: null,
        diplome_licence: null,
        diplome_master_dea_ds: null,
        has_diplome_etat: '',
        has_diplome_graduat: '',
        has_diplome_licence: '',
        has_diplome_master_dea_ds: '',
        universite_master_dea_ds: '',
        pays_master_dea_ds: '',
        date_obtention_master_dea_ds: '',
        universite_obtention_diplome_doctorat: '',
        pays_obtention_diplome_doctorat: '',
        date_obtention_diplome_doctorat: '',
      });
    } catch (error) {
      let errorMsg = 'Erreur lors de la modification';
      if (error.name === 'AbortError') {
        errorMsg = 'La requête a expiré';
      } else if (error.message === 'Failed to fetch') {
        console.error('Erreur de connexion réseau lors de la modification');
        return;
      } else if (error.message) {
        errorMsg = error.message;
      }
      showPopup(errorMsg, 'error');
      console.error('Erreur complète:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Validation des champs obligatoires avant soumission ────────────────
  const validateForm = () => {
    const errors = {};
    const errorMessages = [];

    const req = (field, label) => {
      const val = formData[field];
      if (!val || (typeof val === 'string' && val.trim() === '')) {
        errors[field] = true;
        errorMessages.push(label);
      }
    };

    const reqFile = (field, label) => {
      if (!formData[field]) {
        errors[field] = true;
        errorMessages.push(label);
      }
    };

    const rejectUnsupportedMatricule = (field) => {
      if (isUnsupportedEsuMatricule(formData[field])) {
        errors[field] = UNSUPPORTED_ESU_MATRICULE_MESSAGE;
        errorMessages.push(UNSUPPORTED_ESU_MATRICULE_MESSAGE);
      }
    };

    // Champs communs à tous les types de compte
    req('nom', 'Nom');
    req('postnom', 'Postnom');
    req('sexe', 'Sexe');
    req('lieu_naissance', 'Lieu de Naissance');
    req('date_naissance', 'Date de Naissance');
    if (!formData.informations_vraies) {
      errors['informations_vraies'] = true;
      errorMessages.push("Confirmation des informations (case à cocher)");
    }

    if (['Assistant', 'CT', 'Professeur'].includes(formData.typecompte)) {
      req('has_diplome_etat', "Diplôme d'État ou document équivalent");
      if (formData.has_diplome_etat === 'Oui') {
        reqFile('diplome_etat', "Copie du Diplôme d'État ou document équivalent");
      }
      req('has_diplome_graduat', 'Diplôme de Graduat ou document équivalent');
      if (formData.has_diplome_graduat === 'Oui') {
        reqFile('diplome_graduat', 'Copie du Diplôme de Graduat ou document équivalent');
      }
      req('has_diplome_licence', 'Diplôme de Licence / Médecine');
      if (formData.has_diplome_licence === 'Oui') {
        reqFile('diplome_licence', 'Copie du Diplôme de Licence ou document équivalent');
      }
      req('has_diplome_master_dea_ds', 'Diplôme Master / D.E.A / D.E.S ou document équivalent');
      if (formData.has_diplome_master_dea_ds === 'Oui') {
        reqFile('diplome_master_dea_ds', 'Copie du Diplôme de Master / D.E.A / D.E.S ou document équivalent');
      }
      if (formData.typecompte !== 'Professeur' && formData.has_diplome_master_dea_ds === 'Non') {
        req('has_inscription_dea_des', 'Êtes-vous inscrit au Master/D.E.A/D.E.S ?');
        if (formData.has_inscription_dea_des === 'Oui') {
          req('etablissement_inscription_3cycle', "Établissement d'Inscription au Troisième Cycle");
          reqFile('decision_inscription_ass_ct', "Décision d'Inscription (D.E.A/D.E.S ou Doctorat/Thèse)");
          req('date_inscription', "Date d'inscription au troisième cycle");
          req('statut_apprenant', 'Statut Apprenant');
        }
      }

      if (formData.type_etablissement !== 'Privé') {
        req('salaire_base', 'Salaire de base');
      }
    }

    if (formData.typecompte === 'Assistant') {
      req('date_soutenance', "Date d'Engagement");
      req('universite_attache', "Établissement d'attache");
      req('domaine_recherche', 'Domaine de Recherche');
      req('type_etablissement', "Type d'Établissement");
      if (formData.type_etablissement === 'Public') {
        req('matricule', 'Matricule');
      }
      if (!isPrivateEtablissement(formData.type_etablissement)) {
        rejectUnsupportedMatricule('matricule');
      }
      req('statut', 'Mandat Assistant (Premier ou Deuxième Mandat)');
      req('categorie_assistant', "Catégorie d'Assistant");
      // Validation du centre de recherche si c'est un Assistant de Recherche
      if (formData.categorie_assistant === 'Recherche') {
        req('centre_laboratoire_recherche', 'Centre/Laboratoire de Recherche');
      }
      // req('statut_apprenant', 'Statut Apprenant');
      // req('date_inscription', "Date d'Inscription");
      if (formData.has_diplome_master_dea_ds === 'Oui') {
        req('type_diplome', 'Type de diplôme Master / D.E.A / D.E.S');
        req('universite_master_dea_ds', "Université d'obtention de votre master/D.E.A/D.E.S");
        req('pays_master_dea_ds', "Pays d'obtention de votre Master/D.E.A/D.E.S");
        req('date_obtention_master_dea_ds', "Date d'obtention de votre Master/D.E.A/D.E.S");
      }
      reqFile('photo_passeport', 'Photo Passeport');
      reqFile('decision_nomination_assistant', 'Décision de nomination comme assistant');
    } else if (formData.typecompte === 'CT') {
      req('prenom', 'Prénom');
      req('date_soutenance', "Date d'Engagement");
      req('universite_attache', "Établissement d'attache");
      req('domaine_recherche', 'Domaine de Recherche');
      req('type_etablissement', "Type d'Établissement");
      if (formData.type_etablissement === 'Public') {
        req('matricule', 'Matricule');
      }
      if (!isPrivateEtablissement(formData.type_etablissement)) {
        rejectUnsupportedMatricule('matricule');
      }
      // req('type_diplome', 'Type de diplôme');
      reqFile('arrete_nomination_ct', 'Arrêté de nomination comme CT');
      if (formData.has_diplome_master_dea_ds === 'Oui') {
        req('type_diplome', 'Type de diplôme Master / D.E.A / D.E.S');
        req('universite_master_dea_ds', "Université d'obtention de votre master/D.E.A/D.E.S");
        req('pays_master_dea_ds', "Pays d'obtention de votre Master/D.E.A/D.E.S");
        req('date_obtention_master_dea_ds', "Date d'obtention de votre Master/D.E.A/D.E.S");
      }
      reqFile('photo_passeport', 'Photo Passeport');
    } else {
      // Professeur — seuls les champs strictement requis par le serveur
      req('prenom', 'Prénom');
      if (!isPrivateEtablissement(formData.type_etablissement)) {
        rejectUnsupportedMatricule('matricule_esu');
      }
      if (formData.has_diplome_master_dea_ds === 'Oui') {
        req('type_diplome_dea_des', 'Type de diplôme Master / D.E.A / D.E.S');
      }
    }

    return { errors, errorMessages };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation complète des champs obligatoires
    const { errors, errorMessages } = validateForm();
    if (errorMessages.length > 0) {
      setFieldErrors(errors);
      // Mettre en évidence les champs en erreur via le DOM
      Object.keys(errors).forEach(fieldId => {
        const el = document.getElementById(fieldId);
        if (el) el.classList.add('field-has-error');
      });
      // Scroller vers le premier champ en erreur
      const firstId = Object.keys(errors)[0];
      const firstEl = document.getElementById(firstId);
      if (firstEl) {
        if (typeof errors[firstId] === 'string' && firstEl.setCustomValidity) {
          firstEl.setCustomValidity(errors[firstId]);
          firstEl.reportValidity();
        }
        firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Popup avec la liste des champs manquants
      const listMsg = errorMessages.map((m, i) => `${i + 1}. ${m}`).join('\n');
      showPopup(`Champs obligatoires non remplis :\n${listMsg}`, 'error');
      return;
    }

    setLoading(true);
    setMessage(''); // Effacer les messages précédents
    setMessageType(''); // Réinitialiser le type

    const retryFetch = async (url, options, maxRetries = 1) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const response = await fetch(url, {
            ...options,
            signal: getTimeoutSignal(240000), // 4 minutes timeout
          });
          return response;
        } catch (error) {
          console.error(`❌ Tentative ${i + 1} échouée:`, error.name, error.message);
          if (i === maxRetries - 1) throw error;
          const delayMs = 1000 * (i + 1);
          await new Promise(resolve => setTimeout(resolve, delayMs)); // Attendre avant retry
        }
      }
    };

    try {
      // Créer FormData pour multipart/form-data
      const submitData = new FormData();

      // Ajouter l'ID du compte connecté
      const currentUser = AuthService.getUser();
      if (currentUser && currentUser.id) {
        submitData.append('compte_id', currentUser.id);
      }

      if (formData.typecompte === 'Assistant') {
        // ── Endpoint: POST /api/enseignants/assistant/add/ ────────────────────────
        const etablissementAttache = formData.universite_attache === 'AUTRES'
          ? formData.universite_attache_precisee
          : formData.universite_attache;

        const ASSISTANT_TEXT_MAP = {
          nom:                              formData.nom,
          postnom:                          formData.postnom,
          prenom:                           formData.prenom,
          sexe:                             formData.sexe,
          date_naissance:                   formData.date_naissance,
          lieu_naissance:                   formData.lieu_naissance,
          date_engagement:                  formData.date_soutenance,
          domaine_recherche:                formData.domaine_recherche,
          etablissement_attache:            etablissementAttache,
          mandat_assistant:                 formData.statut.split(' ')[0], // "Premier Mandat" -> "Premier", "Deuxième Mandat" -> "Deuxième"
          etablissement_inscription_3cycle: shouldShowThirdCycleFields ? formData.etablissement_inscription_3cycle : '',
          statut_apprenant:                 shouldShowThirdCycleFields ? formData.statut_apprenant : '',
          date_inscription:                 shouldShowThirdCycleFields ? formData.date_inscription : '',
          type_etablissement:               formData.type_etablissement,
          prime_institutionnelle:           formData.type_etablissement === 'Privé' ? '' : formData.prime_institutionnelle,
          type_diplome:                     formData.type_diplome,
          type_diplome_dea_des:             formData.type_diplome_dea_des,
          categorie_assistant:              formData.categorie_assistant,
          // Optionnels
          salaire_base:                     formData.type_etablissement === 'Privé' ? '' : formData.salaire_base,
          commentaires:                     formData.commentaire_confirmation,
          informations_vraies:              formData.informations_vraies,
          centre_laboratoire_recherche:     formData.centre_laboratoire_recherche,
        };

        // Ajouter matricule seulement si l'établissement n'est pas privé
        if (formData.type_etablissement !== 'Privé' && formData.matricule && formData.matricule.trim() !== '') {
          submitData.append('matricule', formData.matricule);
        }

        Object.entries(ASSISTANT_TEXT_MAP).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            submitData.append(key, value);
          }
        });

        // ── Fichiers API Assistant ──────────────────────────────────────────
        if (formData.photo_passeport)               submitData.append('photo_passeport',      formData.photo_passeport);
        if (formData.decision_nomination_assistant) submitData.append('decision_nomination',  formData.decision_nomination_assistant);
        if (shouldShowThirdCycleFields && formData.decision_inscription_ass_ct) submitData.append('decision_inscription', formData.decision_inscription_ass_ct);
        // Envoyer charge_horaire seulement pour Assistant Académique
        if (formData.charge_horaire && formData.categorie_assistant === 'Academique') submitData.append('charge_horaire', formData.charge_horaire);
        if (formData.diplome_etat)                  submitData.append('diplome_etat',          formData.diplome_etat);
        if (formData.diplome_graduat)               submitData.append('diplome_graduat',       formData.diplome_graduat);
        if (formData.diplome_licence)               submitData.append('diplome_licence',       formData.diplome_licence);
        if (formData.diplome_master_dea_ds)         submitData.append('diplome_master_dea_ds', formData.diplome_master_dea_ds);

      } else if (formData.typecompte === 'CT') {
        // ── Endpoint: POST /api/enseignants/chef-travaux/add/ ─────────────────────────
        const etablissementAttache = formData.universite_attache === 'AUTRES'
          ? formData.universite_attache_precisee
          : formData.universite_attache;

        // ── Obligatoires ────────────────────────────────────────────────────
        const CT_REQUIRED_MAP = {
          nom:                              formData.nom,
          postnom:                          formData.postnom,
          prenom:                           formData.prenom,
          sexe:                             formData.sexe,
          date_naissance:                   formData.date_naissance,
          lieu_naissance:                   formData.lieu_naissance,
          date_engagement:                  formData.date_soutenance,
          domaine_recherche:                formData.domaine_recherche,
          etablissement_attache:            etablissementAttache,
          type_etablissement:               formData.type_etablissement,
          etablissement_inscription_3cycle: shouldShowThirdCycleFields ? formData.etablissement_inscription_3cycle : '',
          statut_apprenant:                 shouldShowThirdCycleFields ? formData.statut_apprenant : '',
          date_inscription:                 shouldShowThirdCycleFields ? formData.date_inscription : '',
          prime_institutionnelle:           formData.type_etablissement === 'Privé' ? '' : formData.prime_institutionnelle,
          salaire_base:                     formData.type_etablissement === 'Privé' ? '' : formData.salaire_base,
          type_diplome:                     formData.type_diplome,
          type_diplome_dea_des:             formData.type_diplome_dea_des,
        };

        Object.entries(CT_REQUIRED_MAP).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            submitData.append(key, value);
          }
        });

        // ── Matricule (saisi par l'utilisateur, sauf établissement privé) ──
        if (formData.type_etablissement !== 'Privé' && formData.matricule) submitData.append('matricule', formData.matricule);

        if (formData.commentaire_confirmation)    submitData.append('commentaires',       formData.commentaire_confirmation);
        if (formData.informations_vraies !== null && formData.informations_vraies !== undefined) {
          submitData.append('informations_vraies', formData.informations_vraies);
        }

        // ── Fichiers API Chef de Travaux ────────────────────────────────────
        if (formData.arrete_nomination_ct)        submitData.append('arrete_nomination',    formData.arrete_nomination_ct);
        if (formData.photo_passeport)             submitData.append('photo_passeport',      formData.photo_passeport);
        if (shouldShowThirdCycleFields && formData.decision_inscription_ass_ct) submitData.append('decision_inscription', formData.decision_inscription_ass_ct);
        if (formData.charge_horaire)              submitData.append('charge_horaire',        formData.charge_horaire);
        if (formData.diplome_etat)                submitData.append('diplome_etat',          formData.diplome_etat);
        if (formData.diplome_graduat)             submitData.append('diplome_graduat',       formData.diplome_graduat);
        if (formData.diplome_licence)             submitData.append('diplome_licence',       formData.diplome_licence);
        if (formData.diplome_master_dea_ds)       submitData.append('diplome_master_dea_ds', formData.diplome_master_dea_ds);

      } else {
        // ── Endpoint: POST /api/bnn/enseignant/add/ (Professeur) ──────────────────────────
        const univAttache = formData.universite_attache === 'AUTRES'
          ? formData.universite_attache_precisee
          : formData.universite_attache;

        const PROF_TEXT_MAP = {
          nom:                        formData.nom,
          postnom:                    formData.postnom,
          prenom:                     formData.prenom,
          sexe:                       formData.sexe,
          type_etablissement:         formData.type_etablissement,
          matricule_esu:              formData.type_etablissement === 'Public' ? formData.matricule_esu : '',
          lieu_naissance:             formData.lieu_naissance,
          date_naissance:             formData.date_naissance,
          grade_actuel:               formData.grade_actuel,
          numero_arrete_equivalence:  formData.numero_arrete_equivalence,
          type_diplome:               formData.type_diplome,
          universite_attache:         univAttache,
          date_engagement:            formData.date_engagement,
          reference_dernier_arrete:   formData.grade_actuel === 'DT' ? '' : formData.reference_dernier_arrete,
          prime_institutionnelle:     formData.type_etablissement === 'Privé' ? '' : formData.prime_institutionnelle,
          salaire_base:               formData.type_etablissement === 'Privé' ? '' : formData.salaire_base,
          possede_diplome:                 formData.possede_diplome,
          domaine_recherche:               formData.domaine_recherche,
          sujet_these:                     formData.sujet_these,
          universite_obtention_diplome_doctorat: formData.universite_obtention_diplome_doctorat,
          pays_obtention_diplome_doctorat:       formData.pays_obtention_diplome_doctorat,
          date_obtention_diplome_doctorat:       formData.date_obtention_diplome_doctorat,
          commentaire_confirmation:        formData.commentaire_confirmation,
          informations_vraies:             formData.informations_vraies,
          universite_master_dea_ds:        formData.universite_master_dea_ds,
          pays_master_dea_ds:              formData.pays_master_dea_ds,
          date_obtention_master_dea_ds:    formData.date_obtention_master_dea_ds,
          type_diplome_dea_des:            formData.type_diplome_dea_des,
        };

        Object.entries(PROF_TEXT_MAP).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            submitData.append(key, value);
          }
        });

        // ── Fichiers API Professeur ─────────────────────────────────────────
        if (formData.photo_identite)           submitData.append('photo_identite',           formData.photo_identite);
        if (formData.copie_diplome)            submitData.append('copie_diplome',            formData.copie_diplome);
        // if (formData.copie_arrete_equivalence) submitData.append('copie_arrete_equivalence', formData.copie_arrete_equivalence);
        if (formData.documents_equivalents)    submitData.append('documents_equivalents',    formData.documents_equivalents);
        if (formData.charge_horaire)           submitData.append('charge_horaire',           formData.charge_horaire);
        if (formData.diplome_etat)             submitData.append('diplome_etat',             formData.diplome_etat);
        if (formData.diplome_graduat)          submitData.append('diplome_graduat',          formData.diplome_graduat);
        if (formData.diplome_licence)          submitData.append('diplome_licence',          formData.diplome_licence);
        if (formData.diplome_master_dea_ds)    submitData.append('diplome_master_dea_ds',    formData.diplome_master_dea_ds);
      } // fin branches Professeur / Assistant / CT
      // Endpoint par type d'enseignant
      const submitUrl = formData.typecompte === 'Assistant'
        ? `${SERVER_URL}/api/enseignants/assistant/add/`
        : formData.typecompte === 'CT'
          ? `${SERVER_URL}/api/enseignants/chef-travaux/add/`
          : `${API_BASE_URL}enseignant/add/`;

      const startTime = performance.now();
      const response = await retryFetch(submitUrl, {
        method: 'POST',
        body: submitData,
        // Ne pas ajouter de Content-Type, FormData le fait automatiquement
      });
      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);

      if (response.ok) {
        const result = await response.json();
        const successMsg = result.message || 'Enregistrement réalisé avec succès';
        const detailsMessage = result.data && result.data.id
          ? ` - ID: ${result.data.id}${result.data.matricule_esu ? `, Matricule: ${result.data.matricule_esu}` : ''}`
          : '';
        
        setSuccessMessage(successMsg + detailsMessage);
        setShowSuccessModal(true);

        // Supprimer le brouillon après enregistrement réussi
        try {
          localStorage.removeItem(DRAFT_KEY);
          localStorage.removeItem(MIGRATED_MATRICULE_KEY);
        } catch (error) {
          console.error('Erreur lors de la suppression du brouillon:', error);
        }

        // Si on vient de MyRecord, y retourner après fermeture du modal de succès
        if (onBackToRecord) {
          setTimeout(() => onBackToRecord(), 2000);
        }

        // Réinitialiser le formulaire
        setFormData({
          nom: '',
          postnom: '',
          prenom: '',
          sexe: '',
          type_etablissement: '',
          matricule_esu: '',
          lieu_naissance: '',
          date_naissance: '',
          grade_actuel: '',
          pays_soutenance: '',
          universite_soutenance: '',
          numero_arrete_equivalence: '',
          copie_arrete_equivalence: null,
          date_soutenance: '',
          type_diplome: '',
          universite_attache: '',
          universite_attache_precisee: '',
          reference_dernier_arrete: '',
          prime_institutionnelle: '',
          salaire_base: '',
          photo_identite: null,
          possede_diplome: '',
          copie_diplome: null,
          documents_equivalents: null,
          charge_horaire: null,
          domaine_recherche: '',
          sujet_these: '',
          universite_obtention_diplome_doctorat: '',
          pays_obtention_diplome_doctorat: '',
          date_obtention_diplome_doctorat: '',
          commentaire_confirmation: '',
          informations_vraies: false,
        });
        setLoading(false);
        return;
      } else {
        let errorMsg = 'Une erreur est survenue lors de la création du professeur';
        let isMatriculeError = false;

        try {
          const result = await response.json();
          // Vérifier si c'est une erreur de matricule existant
          if (response.status === 400 && result.matricule_esu &&
            Array.isArray(result.matricule_esu) &&
            result.matricule_esu.some(msg => msg.includes('existe déjà'))) {
            errorMsg = 'Désolé, il semble que votre matricule existe déjà, pour plus d\'information contacter le conseiller chargé du numérique via\nle numéro : (+243 812 044 120)';
            isMatriculeError = true;
          } else if (result.message) {
            // Extraire le message détaillé du serveur
            errorMsg = result.message;
          } else if (result.error) {
            errorMsg = result.error;
          } else if (result.details) {
            errorMsg = JSON.stringify(result.details, null, 2);
          } else if (typeof result === 'object' && result !== null) {
            // Erreurs de validation DRF : { "field": ["message"] }
            const fieldErrors = Object.entries(result)
              .map(([field, msgs]) => {
                const msgText = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
                return `• ${field} : ${msgText}`;
              })
              .join('\n');
            if (fieldErrors) errorMsg = fieldErrors;
          }

          console.error('❌ Réponse d\'erreur du serveur:', {
            status: response.status,
            statusText: response.statusText,
            message: errorMsg,
            fullResponse: result,
          });
        } catch (e) {
          // Si la réponse n'est pas du JSON valide
          const errorDescriptions = {
            400: 'Données invalides envoyées',
            401: 'Authentification requise',
            403: 'Accès refusé',
            404: 'Endpoint non trouvé',
            409: 'Conflit (données en doublon?)',
            500: 'Erreur serveur interne',
            503: 'Service indisponible',
          };

          errorMsg = errorDescriptions[response.status] || `Erreur HTTP ${response.status}`;
          if (response.statusText) {
            errorMsg += ` (${response.statusText})`;
          }

          console.error('❌ Erreur serveur (pas de JSON):', {
            status: response.status,
            statusText: response.statusText,
            parseError: e.message,
          });
        }

        // Ajouter des conseils selon le type d'erreur (sauf pour le matricule existant qui a déjà son message)
        let userFriendlyMsg = errorMsg;
        if (response.status === 400 && !errorMsg.includes('matricule existe déjà')) {
          userFriendlyMsg += '\n\n💡 Conseil: Vérifiez que tous les champs obligatoires sont remplis correctement.';
        } else if (response.status === 409) {
          userFriendlyMsg += '\n\n💡 Conseil: Ce matricule existe peut-être déjà. Vérifiez le matricule ESU.';
        } else if (response.status >= 500) {
          userFriendlyMsg += '\n\n💡 Conseil: Le serveur rencontre des problèmes. Veuillez réessayer dans quelques instants.';
        }

        // Afficher le modal pour l'erreur matricule, sinon le popup normal
        if (isMatriculeError) {
          setErrorModalMessage(userFriendlyMsg);
          setShowErrorModal(true);
        } else {
          showPopup(userFriendlyMsg, 'error');
        }
        console.error('📢 Message d\'erreur affiché:', userFriendlyMsg);
        setLoading(false);
        return;
      }
    } catch (error) {
      let errorMsg = 'Erreur lors de l\'enregistrement';

      console.error('=== ERREUR COMPLÈTE ===');
      console.error('Nom:', error.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);

      if (error.name === 'AbortError') {
        errorMsg = '⏱️ La requête a expiré (timeout)\n\n' +
          'Le serveur met trop de temps à répondre (plus de 2 minutes).\n\n' +
          '💡 Solutions possibles:\n' +
          '• Les fichiers uploadés sont trop volumineux\n' +
          '• Le serveur est surchargé\n' +
          '• Votre connexion Internet est lente\n\n' +
          'Veuillez réessayer avec des fichiers plus petits.';
      } else if (error.message === 'Failed to fetch') {
        // Ignorer silencieusement les erreurs de connexion réseau
        console.error('Erreur de connexion réseau détectée');
        return;
      } else if (error.message && error.message.includes('CORS')) {
        errorMsg = '🔒 Erreur CORS\n\n' +
          'Le serveur n\'autorise pas l\'accès depuis votre domaine.\n\n' +
          'Contactez l\'administrateur du serveur.';
      } else if (error.message && error.message.includes('JSON')) {
        errorMsg = '⚠️ Réponse invalide du serveur\n\n' +
          'Le serveur a envoyé une réponse mal formatée.\n\n' +
          '💡 Conseil: Contactez le support technique.';
      } else {
        errorMsg = error.message || 'Une erreur inconnue s\'est produite. Veuillez réessayer.';
      }

      showPopup(errorMsg, 'error');
      console.error('❌ Message d\'erreur affiché:', errorMsg);
      setLoading(false);
    }
  };

  const shouldShowThirdCycleFields =
    formData.has_diplome_master_dea_ds === 'Non' && formData.has_inscription_dea_des === 'Oui';

  if (etablissementsLoading) {
    return (
      <div className="professor-registration-form">
        <LoadingModal isVisible message="Chargement des établissements..." />
      </div>
    );
  }

  const renderThirdCycleFields = () => (
    <>
      <div className="form-group">
        <label htmlFor="etablissement_inscription_3cycle">Établissement d'Inscription au Troisième Cycle <span className="required">*</span></label>
        <input
          type="text"
          id="etablissement_inscription_3cycle"
          name="etablissement_inscription_3cycle"
          value={formData.etablissement_inscription_3cycle}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="decision_inscription_ass_ct">Décision d'Inscription (D.E.A/D.E.S ou Doctorat/Thèse) <span className="required">*</span></label>
        <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
        <input
          type="file"
          id="decision_inscription_ass_ct"
          name="decision_inscription_ass_ct"
          accept={ALLOWED_FILE_ACCEPT}
          onChange={handleFileChange}
          required={!hasCurrentFile('decision_inscription_ass_ct')}
        />
        {renderFileStatus('decision_inscription_ass_ct')}
      </div>

      <div className="form-group">
        <label htmlFor="date_inscription">Date d'inscription au troisième cycle <span className="required">*</span></label>
        <input
          type="date"
          id="date_inscription"
          name="date_inscription"
          value={formData.date_inscription}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="statut_apprenant">Statut Apprenant <span className="required">*</span></label>
        <select
          id="statut_apprenant"
          name="statut_apprenant"
          value={formData.statut_apprenant}
          onChange={handleInputChange}
          required
        >
          <option value="">-- Sélectionner --</option>
          <option value="DEA/DES">D.E.A/D.E.S</option>
          <option value="Doctorat">Doctorat</option>
        </select>
      </div>
    </>
  );

  return (
    <div className="professor-registration-form">
      {/* ── Header pleine largeur ── */}
      <header className="form-header">
        <div className="form-header-left">
          <img src="/app-logo.png" alt="Logo MINESURSI" className="form-header-logo" />
          <div className="form-header-titles">
            <h1 className="form-header-title">{(() => {
              const t = formData && formData.typecompte ? formData.typecompte : '';
              if (t === 'Assistant') return "Registre d'Identification des Assistants";
              if (t === 'CT') return "Registre d'Identification des Chefs de Travaux";
              return "Registre d'Identification des Professeurs";
            })()}</h1>
            <p className="form-header-subtitle">{(formData && (formData.typecompte === 'Assistant' || formData.typecompte === 'CT')) ? 'Corps scientifique du MINESURSI' : 'Corps académique du MINESURSI'}</p>
          </div>
        </div>
        <div className="form-header-right">
          {currentUser && (
            <button
              type="button"
              className="mr-user-btn"
              onClick={() => setShowProfileSidebar(true)}
              title="Voir mon profil"
            >
              <UserInfo user={currentUser} />
            </button>
          )}
          <a
            href="/guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="mr-guide-btn"
            title="Consulter le guide d'utilisation"
          >
            Guide
          </a>
          {canUseMessaging && (
            <button
              type="button"
              className="mr-message-nav-btn"
              onClick={onOpenMessaging}
              title="Ouvrir la messagerie"
            >
              <FaComments />
              Messagerie
              {messagingUnreadCount > 0 && (
                <span className="mr-message-badge" aria-label={`${messagingUnreadCount} nouveau message`}>
                  {messagingUnreadCount > 99 ? '99+' : messagingUnreadCount}
                </span>
              )}
            </button>
          )}
          {onLogout && (
            <button
              type="button"
              className="mr-logout-btn"
              onClick={onLogout}
              title="Se déconnecter"
            >
              <FaSignOutAlt /> Déconnexion
            </button>
          )}
        </div>
      </header>

      {/* ── Contenu centré ── */}
      <div className="form-body">

      {message && (
        <div className={`message message-${messageType}`}>
          {message}
        </div>
      )}

      {popupMessage && (
        <div className={`popup-message popup-${popupType}`} role="alert" aria-live="assertive">
          {popupMessage}
        </div>
      )}

      {/* Indicateur de sauvegarde du brouillon */}
      {draftSaved && (
        <div className="draft-saved-indicator">
          ✓ Brouillon sauvegardé
        </div>
      )}

      <div className="form-toolbar">
        {onBackToRecord && (
          <button
            type="button"
            className="btn-back-to-record"
            onClick={onBackToRecord}
          >
            <FaArrowLeft /> Retour à mon dossier
          </button>
        )}
      </div>

      {editMode && (
        <div className="edit-mode-banner">
          Mode édition activé
          {([formData.nom || preloadedRecord?.nom, formData.postnom || preloadedRecord?.postnom].filter(Boolean).join(' ')) &&
            ` — ${[formData.nom || preloadedRecord?.nom, formData.postnom || preloadedRecord?.postnom].filter(Boolean).join(' ')}`
          }
        </div>
      )}

      {/* If no account type is set, ask the parent App to show the global chooser (avoid duplicating) */}
      

      

          {/* Confirmation modal when changing account type */}
          {showTypeChangeModal && (
            <div className="modal-overlay" onClick={() => { setShowTypeChangeModal(false); setPendingTypeSelection(''); }}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Confirmer le changement de type</h2>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => { setShowTypeChangeModal(false); setPendingTypeSelection(''); }}
                  >
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <p>Vous changez le type de compte de <strong>{previousType || 'Non renseigné'}</strong> vers <strong>{pendingTypeSelection}</strong>.</p>
                  <p>Les champs suivants seront effacés et les fichiers associés perdus :</p>
                  <ul>
                    {(() => {
                      const items = [];
                      if (previousType === 'Assistant' && pendingTypeSelection !== 'Assistant') {
                        if (formData.decision_nomination_assistant) items.push('Décision de nomination (assistant)');
                        if (formData.statut) items.push('Statut');
                      }
                      if (previousType === 'CT' && pendingTypeSelection !== 'CT') {
                        if (formData.arrete_nomination_ct) items.push('Arrêté de nomination (CT)');
                      }
                      if (items.length === 0) return <li>Aucun champ sensible détecté.</li>;
                      return items.map((it) => <li key={it}>{it}</li>);
                    })()}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowTypeChangeModal(false);
                      setPendingTypeSelection('');
                      // remettre l'ancien type dans le formulaire
                      setFormData({ ...formData, typecompte: previousType });
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={confirmTypeChange}
                  >
                    Confirmer et effacer
                  </button>
                </div>
              </div>
            </div>
          )}

      <form style={{ display: (!editMode && !formData.typecompte) ? 'none' : 'block' }} onSubmit={editMode ? (e) => { e.preventDefault(); handleEditProfessor(); } : handleSubmit} encType="multipart/form-data" noValidate={editMode}>
        {/* Type de compte (géré via le bouton 'Changer le type de compte') */}
        <input type="hidden" name="typecompte" value={formData.typecompte} />

        {/* Section Informations Assistants (only for Assistant) */}
        {formData.typecompte === 'Assistant' && (
          <fieldset>
            <legend><FaUser /> Informations Assistants</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nom">Nom <span className="required">*</span></label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="postnom">Postnom <span className="required">*</span></label>
                <input
                  type="text"
                  id="postnom"
                  name="postnom"
                  value={formData.postnom}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="prenom">Prénom <span className="required">*</span></label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date_naissance">Date de Naissance <span className="required">*</span></label>
                <input
                  type="date"
                  id="date_naissance"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sexe">Sexe <span className="required">*</span></label>
                <select
                  id="sexe"
                  name="sexe"
                  value={formData.sexe}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="lieu_naissance">Lieu de Naissance <span className="required">*</span></label>
                <input
                  type="text"
                  id="lieu_naissance"
                  name="lieu_naissance"
                  value={formData.lieu_naissance}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="date_soutenance">Date d'Engagement <span className="required">*</span></label>
              <input
                type="date"
                id="date_soutenance"
                name="date_soutenance"
                value={formData.date_soutenance}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="domaine_recherche">Domaine de Recherche <span className="required">*</span></label>
              <input
                type="text"
                id="domaine_recherche"
                name="domaine_recherche"
                value={formData.domaine_recherche}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="categorie_assistant">Catégorie d'Assistant <span className="required">*</span></label>
              <select
                id="categorie_assistant"
                name="categorie_assistant"
                value={formData.categorie_assistant}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                {CATEGORIE_ASSISTANT_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.categorie_assistant === 'Recherche' && (
              <div className="form-group">
                <label htmlFor="centre_laboratoire_recherche">Centre/Laboratoire de Recherche <span className="required">*</span></label>
                <input
                  type="text"
                  id="centre_laboratoire_recherche"
                  name="centre_laboratoire_recherche"
                  value={formData.centre_laboratoire_recherche}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="photo_passeport">Photo Passeport <span className="required">*</span></label>
              <small className="file-hint">{PHOTO_LABEL}</small>
              <input
                type="file"
                id="photo_passeport"
                name="photo_passeport"
                accept={PHOTO_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('photo_passeport')}
              />
              {filePreviews.photo_passeport && (
                <img src={filePreviews.photo_passeport} alt="Aperçu photo passeport" className="file-preview-img" />
              )}
              {!filePreviews.photo_passeport && renderFileStatus('photo_passeport')}
            </div>

            <div className="form-group">
              <label htmlFor="type_etablissement">Type d'Établissement d'Attache (Privé ou Public) <span className="required">*</span></label>
              <select
                id="type_etablissement"
                name="type_etablissement"
                value={formData.type_etablissement}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Public">Établissement Public</option>
                <option value="Privé">Établissement Privé</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="universite_attache">Établissement d'attache <span className="required">*</span></label>
              <Select
                id="universite_attache"
                name="universite_attache"
                className={fieldErrors.universite_attache ? 'select-has-error' : undefined}
                options={universityOptions}
                value={selectedUniversityOption}
                onChange={handleUniversityChange}
                placeholder={universitySelectPlaceholder}
                noOptionsMessage={universityNoOptionsMessage}
                isDisabled={isUniversitySelectDisabled}
                isClearable
              />
            </div>

            {!isPrivateEtablissement(formData.type_etablissement) && (
              <div className="form-group">
                <label htmlFor="matricule">
                  Matricule {formData.type_etablissement === 'Public' ? <span className="required">*</span> : <span className="optional">(optionnel)</span>}
                </label>
                <input
                  type="text"
                  id="matricule"
                  name="matricule"
                  value={formData.matricule}
                  onChange={handleInputChange}
                  required={formData.type_etablissement === 'Public'}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="statut">Assistant Premier ou Deuxième Mandat <span className="required">*</span></label>
              <select
                id="statut"
                name="statut"
                value={formData.statut}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Premier Mandat">Premier Mandat</option>
                <option value="Deuxième Mandat">Deuxième Mandat</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="decision_nomination_assistant">Téléverser la décision de nomination comme assistant premier mandat ou deuxième mandat <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="decision_nomination_assistant"
                name="decision_nomination_assistant"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('decision_nomination_assistant')}
              />
              {renderFileStatus('decision_nomination_assistant')}
            </div>

          </fieldset>
        )}

        {/* Section Documents à Télécharger for Assistant */}
        {formData.typecompte === 'Assistant' && (
        <fieldset>
          <legend><FaFileAlt /> Documents à Télécharger</legend>

          {/* Diplôme d'État */}
          <div className="form-group">
            <label htmlFor="has_diplome_etat_ass">Avez-vous un Diplôme d'État ou document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_etat_ass" name="has_diplome_etat" value={formData.has_diplome_etat} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_etat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_etat_ass">Téléverser la copie du diplôme d'État ou d'un document équivalent. <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_etat_ass" name="diplome_etat" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_etat')} />
              {renderFileStatus('diplome_etat')}
            </div>
          )}

          {/* Diplôme de Graduat */}
          <div className="form-group">
            <label htmlFor="has_diplome_graduat_ass">Avez-vous un Diplôme de Graduat ou Document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_graduat_ass" name="has_diplome_graduat" value={formData.has_diplome_graduat} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_graduat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_graduat_ass">Copie du Diplôme de Graduat ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_graduat_ass" name="diplome_graduat" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_graduat')} />
              {renderFileStatus('diplome_graduat')}
            </div>
          )}

          {/* Diplôme de Licence */}
          <div className="form-group">
            <label htmlFor="has_diplome_licence_ass">Avez-vous un Diplôme de Licence / Medecine ? <span className="required">*</span></label>
            <select id="has_diplome_licence_ass" name="has_diplome_licence" value={formData.has_diplome_licence} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_licence === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_licence_ass">Copie du Diplôme de Licence ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_licence_ass" name="diplome_licence" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_licence')} />
              {renderFileStatus('diplome_licence')}
            </div>
          )}
          {/* Diplôme Master / D.E.A / D.E.S */}
          <div className="form-group">
            <label htmlFor="has_diplome_master_ass">Avez-vous un Diplôme Master / D.E.A / D.E.S  ou Document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_master_ass" name="has_diplome_master_dea_ds" value={formData.has_diplome_master_dea_ds} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_master_dea_ds === 'Non' && (
            <div className="form-group">
              <label htmlFor="has_inscription_dea_des_ass">Êtes-vous inscrit au Master/D.E.A/D.E.S ? <span className="required">*</span></label>
              <select
                id="has_inscription_dea_des_ass"
                name="has_inscription_dea_des"
                value={formData.has_inscription_dea_des}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>
          )}
          {shouldShowThirdCycleFields && renderThirdCycleFields()}
          {formData.has_diplome_master_dea_ds === 'Oui' && (
            <>
              <div className="form-group">
                <label htmlFor="type_diplome_ass">Type de diplôme Master / D.E.A / D.E.S <span className="required">*</span></label>
                <select
                  id="type_diplome_ass"
                  name="type_diplome"
                  value={formData.type_diplome}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Academique">Académique</option>
                  <option value="Professionnel">Professionnel</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="diplome_master_ass">Copie du Diplôme de Master / D.E.A / D.E.S ou Document équivalent <span className="required">*</span></label>
                <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
                <input type="file" id="diplome_master_ass" name="diplome_master_dea_ds" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_master_dea_ds')} />
                {renderFileStatus('diplome_master_dea_ds')}
              </div>
              <div className="form-group">
                <label htmlFor="universite_master_dea_ds_ass">Université d'obtention de votre master/D.E.A/D.E.S <span className="required">*</span></label>
                <input type="text" id="universite_master_dea_ds_ass" name="universite_master_dea_ds" value={formData.universite_master_dea_ds} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="pays_master_dea_ds_ass">Pays d'obtention de votre Master/D.E.A/D.E.S <span className="required">*</span></label>
                <Select
                  id="pays_master_dea_ds_ass"
                  name="pays_master_dea_ds"
                  className={fieldErrors.pays_master_dea_ds ? 'select-has-error' : undefined}
                  options={COUNTRIES}
                  value={formData.pays_master_dea_ds ? COUNTRIES.find(c => c.value === formData.pays_master_dea_ds) : null}
                  onChange={(selectedOption) => handleDiplomaCountryChange('pays_master_dea_ds', selectedOption)}
                  placeholder="Sélectionner ou rechercher un pays..."
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => 'Aucun pays trouvé'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="date_obtention_master_dea_ds_ass">Date d'obtention de votre Master/D.E.A/D.E.S <span className="required">*</span></label>
                <input type="date" id="date_obtention_master_dea_ds_ass" name="date_obtention_master_dea_ds" value={formData.date_obtention_master_dea_ds} onChange={handleInputChange} required />
              </div>
            </>
          )}
        </fieldset>
        )}

        {/* Charge Horaire for Assistant Académique */}
        {formData.typecompte === 'Assistant' && formData.categorie_assistant === 'Academique' && (
          <fieldset>
            <legend>Charge Horaire</legend>
            <div className="form-group">
              <label htmlFor="charge_horaire_ass">Charge horaire validée par une autorité décanale / Académique <span className="optional">(optionnel)</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="charge_horaire_ass"
                name="charge_horaire"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
              />
              {renderFileStatus('charge_horaire')}
            </div>
          </fieldset>
        )}

        {/* Section Informations CT (only for CT) */}
        {formData.typecompte === 'CT' && (
          <fieldset>
            <legend><FaUser /> Informations CT (Chef de Travaux)</legend>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nom">Nom <span className="required">*</span></label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="postnom">Postnom <span className="required">*</span></label>
                <input
                  type="text"
                  id="postnom"
                  name="postnom"
                  value={formData.postnom}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="prenom">Prénom <span className="required">*</span></label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date_naissance">Date de Naissance <span className="required">*</span></label>
                <input
                  type="date"
                  id="date_naissance"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sexe">Sexe <span className="required">*</span></label>
                <select
                  id="sexe"
                  name="sexe"
                  value={formData.sexe}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="lieu_naissance">Lieu de Naissance <span className="required">*</span></label>
                <input
                  type="text"
                  id="lieu_naissance"
                  name="lieu_naissance"
                  value={formData.lieu_naissance}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="date_soutenance">Date d'Engagement <span className="required">*</span></label>
              <input
                type="date"
                id="date_soutenance"
                name="date_soutenance"
                value={formData.date_soutenance}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="domaine_recherche">Domaine de Recherche <span className="required">*</span></label>
              <input
                type="text"
                id="domaine_recherche"
                name="domaine_recherche"
                value={formData.domaine_recherche}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="arrete_nomination_ct">Téléverser votre arrêté de nomination comme CT <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="arrete_nomination_ct"
                name="arrete_nomination_ct"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('arrete_nomination_ct')}
              />
              {renderFileStatus('arrete_nomination_ct')}
            </div>

            <div className="form-group">
              <label htmlFor="photo_passeport">Photo Passeport <span className="required">*</span></label>
              <small className="file-hint">{PHOTO_LABEL}</small>
              <input
                type="file"
                id="photo_passeport"
                name="photo_passeport"
                accept={PHOTO_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('photo_passeport')}
              />
              {filePreviews.photo_passeport && (
                <img src={filePreviews.photo_passeport} alt="Aperçu photo passeport" className="file-preview-img" />
              )}
              {!filePreviews.photo_passeport && renderFileStatus('photo_passeport')}
            </div>

            <div className="form-group">
              <label htmlFor="type_etablissement">Type d'Établissement d'Attache (Privé ou Public) <span className="required">*</span></label>
              <select
                id="type_etablissement"
                name="type_etablissement"
                value={formData.type_etablissement}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Public">Établissement Public</option>
                <option value="Privé">Établissement Privé</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="universite_attache">Établissement d'attache <span className="required">*</span></label>
              <Select
                id="universite_attache"
                name="universite_attache"
                className={fieldErrors.universite_attache ? 'select-has-error' : undefined}
                options={universityOptions}
                value={selectedUniversityOption}
                onChange={handleUniversityChange}
                placeholder={universitySelectPlaceholder}
                noOptionsMessage={universityNoOptionsMessage}
                isDisabled={isUniversitySelectDisabled}
                isClearable
              />
            </div>

            {!isPrivateEtablissement(formData.type_etablissement) && (
              <div className="form-group">
                <label htmlFor="matricule">
                  Matricule {formData.type_etablissement === 'Public' ? <span className="required">*</span> : <span className="optional">(optionnel)</span>}
                </label>
                <input
                  type="text"
                  id="matricule"
                  name="matricule"
                  value={formData.matricule}
                  onChange={handleInputChange}
                  required={formData.type_etablissement === 'Public'}
                />
              </div>
            )}

          </fieldset>
        )}

        {/* Section Documents à Télécharger for CT */}
        {formData.typecompte === 'CT' && (
        <fieldset>
          <legend><FaFileAlt /> Documents à Télécharger</legend>

          {/* Diplôme d'État */}
          <div className="form-group">
            <label htmlFor="has_diplome_etat_ct">Avez-vous un Diplôme d'État ou document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_etat_ct" name="has_diplome_etat" value={formData.has_diplome_etat} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_etat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_etat_ct">Téléverser la copie du diplôme d'État ou d'un document équivalent. <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_etat_ct" name="diplome_etat" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_etat')} />
              {renderFileStatus('diplome_etat')}
            </div>
          )}

          {/* Diplôme de Graduat */}
          <div className="form-group">
            <label htmlFor="has_diplome_graduat_ct">Avez-vous un Diplôme de Graduat ou Document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_graduat_ct" name="has_diplome_graduat" value={formData.has_diplome_graduat} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_graduat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_graduat_ct">Copie du Diplôme de Graduat ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_graduat_ct" name="diplome_graduat" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_graduat')} />
              {renderFileStatus('diplome_graduat')}
            </div>
          )}

          {/* Diplôme de Licence */}
          <div className="form-group">
            <label htmlFor="has_diplome_licence_ct">Avez-vous un Diplôme de Licence / de Médecine ? <span className="required">*</span></label>
            <select id="has_diplome_licence_ct" name="has_diplome_licence" value={formData.has_diplome_licence} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_licence === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_licence_ct">Copie du Diplôme de Licence  ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input type="file" id="diplome_licence_ct" name="diplome_licence" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_licence')} />
              {renderFileStatus('diplome_licence')}
            </div>
          )}
          {/* Diplôme Master / D.E.A / D.E.S */}
          <div className="form-group">
            <label htmlFor="has_diplome_master_ct">Avez-vous un Diplôme Master / D.E.A / D.E.S ou Document équivalent ? <span className="required">*</span></label>
            <select id="has_diplome_master_ct" name="has_diplome_master_dea_ds" value={formData.has_diplome_master_dea_ds} onChange={handleInputChange} required>
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_master_dea_ds === 'Non' && (
            <div className="form-group">
              <label htmlFor="has_inscription_dea_des_ct">Êtes-vous inscrit au Master/D.E.A/D.E.S ? <span className="required">*</span></label>
              <select
                id="has_inscription_dea_des_ct"
                name="has_inscription_dea_des"
                value={formData.has_inscription_dea_des}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>
          )}
          {shouldShowThirdCycleFields && renderThirdCycleFields()}
          {formData.has_diplome_master_dea_ds === 'Oui' && (
            <>
              <div className="form-group">
                <label htmlFor="type_diplome_ct">Type de diplôme Master / D.E.A / D.E.S <span className="required">*</span></label>
                <select
                  id="type_diplome_ct"
                  name="type_diplome"
                  value={formData.type_diplome}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Academique">Académique</option>
                  <option value="Professionnel">Professionnel</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="diplome_master_ct">Copie du Diplôme de Master / D.E.A / D.E.S ou Document équivalent <span className="required">*</span></label>
                <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
                <input type="file" id="diplome_master_ct" name="diplome_master_dea_ds" accept={ALLOWED_FILE_ACCEPT} onChange={handleFileChange} required={!hasCurrentFile('diplome_master_dea_ds')} />
                {renderFileStatus('diplome_master_dea_ds')}
              </div>
              <div className="form-group">
                <label htmlFor="universite_master_dea_ds_ct">Université d'obtention de votre Master/D.E.A/D.E.S <span className="required">*</span></label>
                <input type="text" id="universite_master_dea_ds_ct" name="universite_master_dea_ds" value={formData.universite_master_dea_ds} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="pays_master_dea_ds_ct">Pays d'obtention de votre Master/D.E.A/D.E.S <span className="required">*</span></label>
                <Select
                  id="pays_master_dea_ds_ct"
                  name="pays_master_dea_ds"
                  className={fieldErrors.pays_master_dea_ds ? 'select-has-error' : undefined}
                  options={COUNTRIES}
                  value={formData.pays_master_dea_ds ? COUNTRIES.find(c => c.value === formData.pays_master_dea_ds) : null}
                  onChange={(selectedOption) => handleDiplomaCountryChange('pays_master_dea_ds', selectedOption)}
                  placeholder="Sélectionner ou rechercher un pays..."
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => 'Aucun pays trouvé'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="date_obtention_master_dea_ds_ct">Date d'obtention de votre Master/D.E.A/D.E.S <span className="required">*</span></label>
                <input type="date" id="date_obtention_master_dea_ds_ct" name="date_obtention_master_dea_ds" value={formData.date_obtention_master_dea_ds} onChange={handleInputChange} required />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="charge_horaire_ct">Charge horaire validée par une autorité décanale / Académique <span className="optional">(optionnel)</span></label>
            <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
            <input
              type="file"
              id="charge_horaire_ct"
              name="charge_horaire"
              accept={ALLOWED_FILE_ACCEPT}
              onChange={handleFileChange}
            />
            {renderFileStatus('charge_horaire')}
          </div>
        </fieldset>
        )}

        {/* Section Informations Personnelles (only for Professeur) */}
        {formData.typecompte === 'Professeur' && (
        <fieldset>
          <legend><FaUser /> Informations Personnelles</legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nom">Nom <span className="required">*</span></label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="postnom">Postnom <span className="required">*</span></label>
              <input
                type="text"
                id="postnom"
                name="postnom"
                value={formData.postnom}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="prenom">Prénom <span className="required">*</span></label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                value={formData.prenom}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="sexe">Sexe <span className="required">*</span></label>
              <select
                id="sexe"
                name="sexe"
                value={formData.sexe}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="lieu_naissance">Lieu de Naissance <span className="required">*</span></label>
            <input
              type="text"
              id="lieu_naissance"
              name="lieu_naissance"
              value={formData.lieu_naissance}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="date_naissance">Date de Naissance <span className="required">*</span></label>
            <input
              type="date"
              id="date_naissance"
              name="date_naissance"
              value={formData.date_naissance}
              onChange={handleInputChange}
              required
            />
          </div>

        </fieldset>
        )}

        {/* All other sections for Professeur only */}
        {formData.typecompte === 'Professeur' && (
        <>
        {/* Section Informations Administratives */}
        <fieldset>
          <legend><FaGraduationCap /> Informations Administratives</legend>

          <div className="form-group">
            <label htmlFor="type_etablissement">Type d'Établissement d'Attache (Privé ou Public) <span className="required">*</span></label>
            <select
              id="type_etablissement"
              name="type_etablissement"
              value={formData.type_etablissement}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Public">Établissement Public</option>
              <option value="Privé">Établissement Privé</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="universite_attache">Etablissement d'Attache <span className="required">*</span></label>
            <Select
              id="universite_attache"
              name="universite_attache"
              className={fieldErrors.universite_attache ? 'select-has-error' : undefined}
              options={universityOptions}
              value={selectedUniversityOption}
              onChange={handleUniversityChange}
              placeholder={universitySelectPlaceholder}
              noOptionsMessage={universityNoOptionsMessage}
              searchable={true}
              isSearchable={true}
              isDisabled={isUniversitySelectDisabled}
              isClearable={false}
              required
            />
          </div>

          {!isPrivateEtablissement(formData.type_etablissement) && formData.type_etablissement === 'Public' && (
            <div className="form-group">
              <label htmlFor="matricule_esu">Matricule ESU <span className="required">*</span></label>
              <input
                type="text"
                id="matricule_esu"
                name="matricule_esu"
                value={formData.matricule_esu}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          {formData.typecompte === 'Professeur' && (
            <div className="form-group">
              <label htmlFor="date_engagement">Date d'Engagement <span className="required">*</span></label>
              <input
                type="date"
                id="date_engagement"
                name="date_engagement"
                value={formData.date_engagement}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          {formData.typecompte === 'Assistant' && (
            <div className="form-group">
                <label htmlFor="statut">Statut {formData.typecompte === 'Assistant' ? <span className="required">(requis)</span> : <span className="optional">(optionnel)</span>}</label>
              <select
                id="statut"
                name="statut"
                value={formData.statut}
                onChange={handleInputChange}
                required={formData.typecompte === 'Assistant'}
              >
                <option value="">-- Sélectionner --</option>
                <option value="Assistant premier mandat">Assistant premier mandat</option>
                <option value="Assistant deuxième mandat">Assistant deuxième mandat</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="grade_actuel">Qualité ou Grade Académique <span className="required">*</span></label>
            <select
              id="grade_actuel"
              name="grade_actuel"
              value={formData.grade_actuel}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="PE">Professeur Émérite</option>
              <option value="PO">Professeur Ordinaire</option>
              <option value="P">Professeur</option>
              <option value="PA">Professeur Associé</option>
              <option value="DT">Docteur à thèse</option>
            </select>
          </div>

          {formData.universite_attache === 'AUTRES' && (
            <div className="form-group">
              <label htmlFor="universite_attache_precisee">Préciser l'Etablissement d'attache <span className="required">*</span></label>
              <input
                type="text"
                id="universite_attache_precisee"
                name="universite_attache_precisee"
                value={formData.universite_attache_precisee}
                onChange={handleInputChange}
                required={formData.universite_attache === 'AUTRES'}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="domaine_recherche">Domaine de Recherche <span className="required">*</span></label>
            <input
              type="text"
              id="domaine_recherche"
              name="domaine_recherche"
              value={formData.domaine_recherche}
              onChange={handleInputChange}
              required
            />
          </div>

          {formData.grade_actuel !== 'DT' && (
            <div className="form-group">
              <label htmlFor="reference_dernier_arrete">Numéro de Référence du Dernier Arrêté <span className="required">*</span></label>
              <input
                type="text"
                id="reference_dernier_arrete"
                name="reference_dernier_arrete"
                value={formData.reference_dernier_arrete}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="numero_arrete_equivalence">Numéro de l'arrêté d'équivalence (si la thèse a été soutenue à l'étranger) <span className="optional">(optionnel)</span></label>
            <input
              type="text"
              id="numero_arrete_equivalence"
              name="numero_arrete_equivalence"
              value={formData.numero_arrete_equivalence}
              onChange={handleInputChange}
            />
          </div>

          {/* copie_arrete_equivalence désactivé
          <div className="form-group">
            <label htmlFor="copie_arrete_equivalence">Copie Arrêté Équivalence <span className="optional">(optionnel)</span></label>
            <input
              type="file"
              id="copie_arrete_equivalence"
              name="copie_arrete_equivalence"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={handleFileChange}
            />
          </div>
          */}
        </fieldset>

        {/* Section Documents à Télécharger (only for Professeur) */}
        {formData.typecompte === 'Professeur' && (
        <fieldset>
          <legend><FaFileAlt /> Documents à Télécharger</legend>

          {/* Diplôme d'État */}
          <div className="form-group">
            <label htmlFor="has_diplome_etat">Avez-vous un Diplôme d'État ou document équivalent ? <span className="required">*</span></label>
            <select
              id="has_diplome_etat"
              name="has_diplome_etat"
              value={formData.has_diplome_etat}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_etat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_etat">Téléverser la copie du diplôme d'État ou d'un document équivalent. <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="diplome_etat"
                name="diplome_etat"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('diplome_etat')}
              />
              {renderFileStatus('diplome_etat')}
            </div>
          )}

          {/* Diplôme de Graduat */}
          <div className="form-group">
            <label htmlFor="has_diplome_graduat">Avez-vous un Diplôme de Graduat ou Document équivalent ? <span className="required">*</span></label>
            <select
              id="has_diplome_graduat"
              name="has_diplome_graduat"
              value={formData.has_diplome_graduat}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_graduat === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_graduat">Copie du Diplôme de Graduat ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="diplome_graduat"
                name="diplome_graduat"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('diplome_graduat')}
              />
              {renderFileStatus('diplome_graduat')}
            </div>
          )}

          {/* Diplôme de Licence */}
          <div className="form-group">
            <label htmlFor="has_diplome_licence">Avez-vous un Diplôme de Licence / de Médecine ? <span className="required">*</span></label>
            <select
              id="has_diplome_licence"
              name="has_diplome_licence"
              value={formData.has_diplome_licence}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_licence === 'Oui' && (
            <div className="form-group">
              <label htmlFor="diplome_licence">Copie du Diplôme de Licence ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="diplome_licence"
                name="diplome_licence"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={!hasCurrentFile('diplome_licence')}
              />
              {renderFileStatus('diplome_licence')}
            </div>
          )}

          {/* Diplôme Master / D.E.A / D.E.S */}
          <div className="form-group">
            <label htmlFor="has_diplome_master_dea_ds">Avez-vous un Diplôme de Master / D.E.A / D.E.S ? <span className="required">*</span></label>
            <select
              id="has_diplome_master_dea_ds"
              name="has_diplome_master_dea_ds"
              value={formData.has_diplome_master_dea_ds}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          {formData.has_diplome_master_dea_ds === 'Oui' && (
            <>
              <div className="form-group">
                <label htmlFor="type_diplome_dea_des">Type de diplôme Master / D.E.A / D.E.S <span className="required">*</span></label>
                <select
                  id="type_diplome_dea_des"
                  name="type_diplome_dea_des"
                  value={formData.type_diplome_dea_des || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Academique">Académique</option>
                  <option value="Professionnel">Professionnel</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="diplome_master_dea_ds">Copie du Diplôme de Master / D.E.A / D.E.S ou Document équivalent <span className="required">*</span></label>
                <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
                <input
                  type="file"
                  id="diplome_master_dea_ds"
                  name="diplome_master_dea_ds"
                  accept={ALLOWED_FILE_ACCEPT}
                  onChange={handleFileChange}
                  required={!hasCurrentFile('diplome_master_dea_ds')}
                />
                {renderFileStatus('diplome_master_dea_ds')}
              </div>
              <div className="form-group">
                <label htmlFor="universite_master_dea_ds">Université d'obtention de votre master/D.E.A/D.E.S <span className="optional">(optionnel)</span></label>
                <input
                  type="text"
                  id="universite_master_dea_ds"
                  name="universite_master_dea_ds"
                  value={formData.universite_master_dea_ds}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pays_master_dea_ds">Pays d'obtention de votre Master/D.E.A/D.E.S <span className="optional">(optionnel)</span></label>
                <Select
                  id="pays_master_dea_ds"
                  name="pays_master_dea_ds"
                  className={fieldErrors.pays_master_dea_ds ? 'select-has-error' : undefined}
                  options={COUNTRIES}
                  value={formData.pays_master_dea_ds ? COUNTRIES.find(c => c.value === formData.pays_master_dea_ds) : null}
                  onChange={(selectedOption) => handleDiplomaCountryChange('pays_master_dea_ds', selectedOption)}
                  placeholder="Sélectionner ou rechercher un pays..."
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => 'Aucun pays trouvé'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="date_obtention_master_dea_ds">Date d'obtention de votre Master/D.E.A/D.E.S <span className="optional">(optionnel)</span></label>
                <input
                  type="date"
                  id="date_obtention_master_dea_ds"
                  name="date_obtention_master_dea_ds"
                  value={formData.date_obtention_master_dea_ds}
                  onChange={handleInputChange}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="possede_diplome">Possédez-vous un doctorat ? <span className="required">*</span></label>
            <select
              id="possede_diplome"
              name="possede_diplome"
              value={formData.possede_diplome}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Sélectionner --</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>

          {formData.possede_diplome === 'Oui' && (
            <>
            <div className="form-group">
              <label htmlFor="type_diplome">Type de diplôme de Doctorat <span className="optional">(optionnel)</span></label>
              <select
                id="type_diplome"
                name="type_diplome"
                value={formData.type_diplome}
                onChange={handleInputChange}
              >
                <option value="">-- Sélectionner --</option>
                <option value="Academique">Académique</option>
                <option value="Professionnel">Professionnel</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="universite_obtention_diplome_doctorat">Université d'obtention de votre Doctorat <span className="optional">(optionnel)</span></label>
              <input
                type="text"
                id="universite_obtention_diplome_doctorat"
                name="universite_obtention_diplome_doctorat"
                value={formData.universite_obtention_diplome_doctorat}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="pays_obtention_diplome_doctorat">Pays d'obtention de votre Doctorat <span className="optional">(optionnel)</span></label>
              <Select
                id="pays_obtention_diplome_doctorat"
                name="pays_obtention_diplome_doctorat"
                className={fieldErrors.pays_obtention_diplome_doctorat ? 'select-has-error' : undefined}
                options={COUNTRIES}
                value={formData.pays_obtention_diplome_doctorat ? COUNTRIES.find(c => c.value === formData.pays_obtention_diplome_doctorat) : null}
                onChange={(selectedOption) => handleDiplomaCountryChange('pays_obtention_diplome_doctorat', selectedOption)}
                placeholder="Sélectionner ou rechercher un pays..."
                isSearchable={true}
                isClearable={true}
                noOptionsMessage={() => 'Aucun pays trouvé'}
              />
            </div>
            <div className="form-group">
              <label htmlFor="date_obtention_diplome_doctorat">Date d'obtention de votre Doctorat <span className="optional">(optionnel)</span></label>
              <input
                type="date"
                id="date_obtention_diplome_doctorat"
                name="date_obtention_diplome_doctorat"
                value={formData.date_obtention_diplome_doctorat}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="sujet_these">Sujet ou intitulé de votre thèse (Doctorat) <span className="required">*</span></label>
              <textarea
                id="sujet_these"
                name="sujet_these"
                value={formData.sujet_these}
                onChange={handleInputChange}
                rows="3"
                maxLength="200"
                required
              ></textarea>
              <small className="char-count">{formData.sujet_these.length}/200 caractères</small>
            </div>
            <div className="form-group">
              <label htmlFor="copie_diplome">Copie du Diplôme de Doctorat ou Document équivalent <span className="required">*</span></label>
              <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
              <input
                type="file"
                id="copie_diplome"
                name="copie_diplome"
                accept={ALLOWED_FILE_ACCEPT}
                onChange={handleFileChange}
                required={formData.possede_diplome === 'Oui' && !hasCurrentFile('copie_diplome')}
              />
              {renderFileStatus('copie_diplome')}
            </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="charge_horaire">Charge horaire validée par une autorité décanale / Académique <span className="required">*</span></label>
            <small className="file-hint">{ALLOWED_FILE_LABEL}</small>
            <input
              type="file"
              id="charge_horaire"
              name="charge_horaire"
              accept={ALLOWED_FILE_ACCEPT}
              onChange={handleFileChange}
              required={!hasCurrentFile('charge_horaire')}
            />
            {renderFileStatus('charge_horaire')}
          </div>

          <div className="form-group">
            <label htmlFor="photo_identite">Photo Passeport <span className="required">*</span></label>
            <small className="file-hint">{PHOTO_LABEL}</small>
            <input
              type="file"
              id="photo_identite"
              name="photo_identite"
              accept={PHOTO_ACCEPT}
              onChange={handleFileChange}
              required={!hasCurrentFile('photo_identite')}
            />
            {filePreviews.photo_identite && (
              <img src={filePreviews.photo_identite} alt="Aperçu photo passeport" className="file-preview-img" />
            )}
            {!filePreviews.photo_identite && renderFileStatus('photo_identite')}
          </div>

        </fieldset>
        )}

        </>
        )}

        {/* Section Informations Financières - Common to all types */}
        {!isPrivateEtablissement(formData.type_etablissement) && (
        <fieldset>
          <legend><FaFileAlt /> Informations Financières</legend>

            <div className="form-group">
              <label htmlFor="prime_institutionnelle">Avez-vous une prime institutionnelle ? <span className="required">*</span></label>
              <select
                id="prime_institutionnelle"
                name="prime_institutionnelle"
                value={formData.prime_institutionnelle}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="salaire_base"> Avez-vous un salaire de Base ? <span className="required">*</span></label>
              <select
                id="salaire_base"
                name="salaire_base"
                value={formData.salaire_base}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>
        </fieldset>
        )}

        {/* Section Confirmation - Common to all types */}
        <fieldset>
          <legend><FaCheckCircle /> Confirmation</legend>

          <div className="form-group">
            <label htmlFor="commentaire_confirmation">Commentaires <span className="optional">(optionnel)</span></label>
            <textarea
              id="commentaire_confirmation"
              name="commentaire_confirmation"
              value={formData.commentaire_confirmation}
              onChange={handleInputChange}
              rows="3"
              maxLength="250"
            ></textarea>
            <small className="char-count">{formData.commentaire_confirmation.length}/250 caractères</small>
          </div>

          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="informations_vraies"
              name="informations_vraies"
              checked={formData.informations_vraies}
              onChange={handleInputChange}
              required
            />
            <label htmlFor="informations_vraies">
              J'affirme que toutes les informations fournies sont vraies et exactes <span className="required">*</span>
            </label>
          </div>
        </fieldset>

        <div className="form-actions">
          <button
            type="submit"
            disabled={loading}
            className="btn-submit"
          >
            {loading ? 'Envoi en cours...' : editMode ? 'Enregistrer les modifications' : 'Valider les informations'}
          </button>
          <button
            type="button"
            className="btn-reset"
            onClick={() => {
              if (editMode) {
                setEditMode(false);
                setChangedFields({});
              } else {
                setFormData({ ...defaultFormState, typecompte: formData.typecompte });
                setFieldErrors({});
                setFilePreviews({});
                // Vider les inputs file du DOM
                document.querySelectorAll('input[type="file"]').forEach(el => { el.value = ''; });
                // Effacer le brouillon local
                try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
              }
            }}
          >
            {editMode ? 'Annuler l\'édition' : 'Réinitialiser'}
          </button>
        </div>
      </form>

      {/* Modal pour voir les données */}
      {showViewModal && (
        <div className="modal-overlay" onClick={handleCloseViewModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Consulter vos informations</h2>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewModal}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {!viewedData ? (
                <div className="modal-search">
                  <div className="form-group">
                    <label htmlFor="view-matricule">Matricule <span className="required">*</span></label>
                    <input
                      type="text"
                      id="view-matricule"
                      value={viewMatricule}
                      onChange={(e) => setViewMatricule(e.target.value)}
                      placeholder="Ex: 099300"
                      onKeyPress={(e) => e.key === 'Enter' && handleViewData()}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-search"
                    onClick={handleViewData}
                    disabled={viewLoading}
                  >
                    {viewLoading ? 'Recherche...' : 'Rechercher'}
                  </button>
                </div>
              ) : (
                <>
                  {renderModalContent()}
                </>
              )}
            </div>

            <div className="modal-footer">
              {viewedData && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleStartEdit}
                >
                  Modifier
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmer la suppression</h2>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelDelete}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce professeur ? Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelDelete}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteProfessor}
              >
                Confirmer la suppression
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Matricule Error */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="modal modal-error" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Matricule existant</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowErrorModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="error-message">{errorModalMessage}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowErrorModal(false)}
              >
                J'ai compris, fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal modal-success" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-success-header">
              <h2>✓ Succès!</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowSuccessModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="success-icon">✓</div>
              <p className="success-message">{successMessage}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-success"
                onClick={() => {
                  setShowSuccessModal(false);
                  if (onRequestAccountTypeReset) {
                    onRequestAccountTypeReset();
                  }
                }}
              >
                Retour au choix du compte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      <LoadingModal
        isVisible={loading}
        message="Enregistrement en cours"
      />

      {/* Profile Sidebar */}
      <ProfileSidebar
        isOpen={showProfileSidebar}
        onClose={() => setShowProfileSidebar(false)}
        user={currentUser}
        onViewMyRecord={() => setShowViewModal(true)}
        onLogout={onLogout}
      />
      </div>
    </div>
  );
};

export default ProfessorRegistrationForm;
